import { useCallback, useEffect, useRef, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { adminService } from "../../features/admin/service";
import { pollFlyerStatus } from "../../features/admin/pollFlyerStatus";
import { dealsService } from "../../features/deals/service";
import type { PaginatedResponse } from "../../features/admin/types";
import { formatIST } from "../../utils/dateUtils";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Pagination from "../../components/admin/Pagination";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import DateRangeFilter, { type DateFilterEntry } from "../../components/admin/DateRangeFilter";
import { env } from "../../config/env";
import { useAdminToast } from "../../context/AdminToastContext";
import { useAlertModal } from "../../components/admin/AlertModal";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";

const DEAL_DATE_FIELDS = [
  { value: "created_at", label: "Created" },
  { value: "updated_at", label: "Updated" },
];

/** Relative /uploads/ paths are served from the backend; everything else
 *  (full http(s) URLs, presigned S3 links) is used as-is. */
const resolveMediaUrl = (u?: string): string | undefined => {
  if (!u) return undefined;
  if (u.startsWith("/uploads/")) return `${env.apiBaseUrl}${u}`;
  return u;
};

/** True for video media (extension, ignoring query string, or known paths). */
const isVideoMedia = (u: string): boolean =>
  /\.(mp4|webm|mov|avi)(\?|$)/i.test(u) || u.includes("/videos/") || u.includes("video/upload");

const TYPE_FILTERS: { label: string; collectionName: string | null }[] = [
  { label: "All", collectionName: null },
  { label: "PACKAGE", collectionName: "deal_packages" },
  { label: "FLIGHT", collectionName: "deal_flights" },
  { label: "HOTEL", collectionName: "deal_stays" },
  { label: "CRUISE", collectionName: "deal_cruises" },
  { label: "TRANSFER", collectionName: "deal_transfers" },
  { label: "SERVICE", collectionName: "deal_services" },
  { label: "EXPERIENCE", collectionName: "deal_experiences" },
];

const STATUS_FILTERS: { label: string; isActive: boolean | null }[] = [
  { label: "All", isActive: null },
  { label: "Active", isActive: true },
  { label: "Inactive", isActive: false },
];

/** Independent of publish/active state — a deal can be stale AND active/inactive.
 *  "No Date" is its own state — a deal with no parseable travel date anywhere
 *  is never counted as stale or upcoming. */
const TRAVEL_STATUS_FILTERS: { label: string; travelStatus: "stale" | "upcoming" | "no_date" | null }[] = [
  { label: "All Dates", travelStatus: null },
  { label: "Stale (Dates Crossed)", travelStatus: "stale" },
  { label: "Upcoming/Active Dates", travelStatus: "upcoming" },
  { label: "No Date Present", travelStatus: "no_date" },
];

interface FilterChip {
  type: "country" | "city" | "category" | "tag" | "company";
  value: string;
}

interface FilterOptions {
  tags: string[];
  countries: string[];
  cities: string[];
  categories: string[];
}

const CHIP_COLORS: Record<FilterChip["type"], string> = {
  country: "bg-sky-500/20 text-sky-300 border-sky-500/30",
  city: "bg-violet-500/20 text-violet-300 border-violet-500/30",
  category: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  tag: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  company: "bg-pink-500/20 text-pink-300 border-pink-500/30",
};

const CHIP_LABEL: Record<FilterChip["type"], string> = {
  country: "Country",
  city: "City",
  category: "Category",
  tag: "Tag",
  company: "Company",
};

const FILTER_SECTIONS: { key: keyof FilterOptions; type: FilterChip["type"]; label: string }[] = [
  { key: "categories", type: "category", label: "Categories" },
  { key: "countries", type: "country", label: "Countries" },
  { key: "cities", type: "city", label: "Cities" },
  { key: "tags", type: "tag", label: "Tags" },
];

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{title}</p>
    {children}
  </div>
);

const ChipList: React.FC<{ items: string[]; className: string }> = ({ items, className }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((it) => (
      <span key={it} className={`text-xs px-2 py-0.5 rounded ${className}`}>{it}</span>
    ))}
  </div>
);

const MetaField: React.FC<{ label: string; value?: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div>
    <p className="text-[11px] text-gray-500">{label}</p>
    <p className={`text-sm text-gray-300 break-all ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</p>
  </div>
);

// ── Generic structured-data renderer ────────────────────────────────────────
// A typed deal carries a different nested block per item_type (package, stay,
// flight, cruise, experience, transfer, media_asset, visa/insurance/custom)
// plus per-type pricing grids, terms, inventory and extras. Hand-writing a
// renderer per block would go stale the moment a schema field is added, so the
// detail modal renders these blocks generically: scalars as label/value rows,
// string arrays as chips, object arrays as small cards. Nothing that exists on
// the document is silently dropped.
const humanizeKey = (key: string): string =>
  key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const isEmptyValue = (v: unknown): boolean => {
  if (v === null || v === undefined || v === "") return true;
  if (Array.isArray(v)) return v.length === 0 || v.every(isEmptyValue);
  if (isPlainObject(v)) return Object.values(v).every(isEmptyValue);
  return false;
};

const formatScalar = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : String(v);
  const s = String(v);
  if (ISO_DATETIME_RE.test(s)) {
    try { return formatIST(s); } catch { return s; }
  }
  return s;
};

const StructuredValue: React.FC<{ value: unknown; depth?: number }> = ({ value, depth = 0 }) => {
  if (Array.isArray(value)) {
    const scalars = value.every((v) => !isPlainObject(v) && !Array.isArray(v));
    if (scalars) {
      // Not ChipList: list values can repeat (e.g. duplicate tags), and it keys on the value.
      return (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v, i) => (
            <span key={i} className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-300">{formatScalar(v)}</span>
          ))}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        {value.map((entry, i) => (
          <div key={i} className="rounded-lg border border-gray-800 bg-gray-950/40 p-2.5">
            <StructuredValue value={entry} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(([, v]) => !isEmptyValue(v));
    if (entries.length === 0) return <p className="text-sm text-gray-500">—</p>;
    return (
      <div className="flex flex-col gap-2">
        {entries.map(([k, v]) => {
          const nested = Array.isArray(v) || isPlainObject(v);
          return (
            <div key={k} className={nested ? "" : "flex flex-wrap items-baseline gap-2"}>
              <p className="text-[10px] uppercase tracking-wide text-gray-500 shrink-0">{humanizeKey(k)}</p>
              {nested ? (
                <div className="mt-1 pl-2 border-l border-gray-800">
                  <StructuredValue value={v} depth={depth + 1} />
                </div>
              ) : (
                <p className="text-sm text-gray-300 break-words whitespace-pre-wrap">{formatScalar(v)}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  return <p className="text-sm text-gray-300 break-words whitespace-pre-wrap">{formatScalar(value)}</p>;
};

/** Section wrapper that renders nothing when the block has no usable data. */
const StructuredSection: React.FC<{ title: string; value: unknown }> = ({ title, value }) => {
  if (isEmptyValue(value)) return null;
  return (
    <Section title={title}>
      <StructuredValue value={value} />
    </Section>
  );
};

/** The per-item_type nested blocks a typed deal can carry (only one is ever
 *  populated on a given deal). See backend/app/modules/deals/typed/schemas/. */
const TYPED_DEAL_BLOCK_KEYS = [
  "package", "stay", "flight", "cruise", "experience", "transfer",
  "media_asset", "visa", "insurance", "custom",
] as const;

/** Top-level keys already rendered by a dedicated section of the detail modal —
 *  everything else falls through to the "Other fields" catch-all so a newly
 *  added schema field shows up without a code change here. */
const RENDERED_DEAL_KEYS = new Set<string>([
  "_id", "id", "title", "subtitle", "description", "summary", "user_title", "user_description",
  "ai_suggested_title", "ai_suggested_description", "enriched_summary", "ai_description",
  "pricing", "destination", "categories", "category", "category_slug", "tags", "activities",
  "media", "gallery", "image", "image_clean", "pdf_url", "video_url", "ai_generated_flyer",
  "dealer", "dealer_id", "dealer_name", "seller_id", "seller_name", "collection", "collection_name",
  "lifecycle", "admin_review", "created_at", "updated_at", "created_by", "updated_by",
  "likes", "save_count", "admin_likes_boost", "admin_saves_boost",
  "terms", "inventory", "extras", "validity",
  "package", "stay", "flight", "cruise", "experience", "transfer", "media_asset",
  "visa", "insurance", "custom", "service_type", "item_type",
  "_broken_fields", "ai_extracted_raw", "ai_extracted",
]);

/** Score → color: Pinecone cosine similarity runs 0..1, higher = closer match. */
const scoreColor = (score: number): string =>
  score >= 0.85 ? "text-emerald-400" : score >= 0.6 ? "text-amber-400" : "text-gray-400";

const PineconeTab: React.FC<{
  loading: boolean;
  error: string | null;
  status: import("../../features/admin/types").DealPineconeStatus | null;
  onReindex: () => void;
  reindexing: boolean;
  reindexNotice: string | null;
}> = ({ loading, error, status, onReindex, reindexing, reindexNotice }) => {
  const [openChunk, setOpenChunk] = useState<string | null>(null);

  if (loading) return <p className="text-sm text-gray-500">Loading Pinecone status…</p>;
  if (error) return <p className="text-sm text-red-400">{error}</p>;
  if (!status) return null;

  if (!status.namespace) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-amber-400">Not indexed</p>
        <p className="text-xs text-gray-500">{status.reason || "This deal has no Pinecone namespace."}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Section title="Index Summary">
        {reindexNotice && <p className="text-xs text-emerald-400 mb-2">{reindexNotice}</p>}
        <div className="flex items-center justify-between mb-3">
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${status.indexed ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${status.indexed ? "bg-green-400" : "bg-red-400"}`} />
            {status.indexed ? "Indexed" : "Not indexed"}
          </span>
          <button
            onClick={onReindex}
            disabled={reindexing}
            className="px-2.5 py-1 rounded-md text-xs font-medium bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 transition-colors disabled:opacity-50"
          >
            {reindexing ? "Reindexing…" : "Reindex now"}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <MetaField label="Indexed" value={status.indexed ? "Yes" : "No"} />
          <MetaField label="Namespace" value={status.namespace ?? undefined} mono />
          <MetaField
            label="Chunks indexed"
            value={`${status.indexed_chunk_count ?? 0} / ${status.chunk_count ?? 0}`}
          />
          <MetaField label="Embedding model" value={status.embedding_model} mono />
          <MetaField label="Vector dimension" value={status.embedding_dimension ? String(status.embedding_dimension) : undefined} />
        </div>
      </Section>

      {status.namespaces && status.namespaces.length > 1 && (
        <Section title={`Namespaces (${status.namespaces.length})`}>
          <p className="text-xs text-gray-500 mb-2">
            This deal is also cross-indexed into secondary namespaces from its secondary
            category slugs — reindexing above covers all of them.
          </p>
          <div className="space-y-1.5">
            {status.namespaces.map((ns) => (
              <div
                key={ns.namespace}
                className="flex items-center justify-between gap-2 text-xs bg-gray-900 border border-gray-800 rounded-lg px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-gray-300 truncate">{ns.namespace}</span>
                  {ns.is_primary && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-400 shrink-0">primary</span>
                  )}
                  {!ns.is_primary && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 shrink-0">secondary</span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-gray-500">{ns.indexed_chunk_count}/{ns.chunk_count} chunks</span>
                  <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${ns.indexed ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${ns.indexed ? "bg-green-400" : "bg-red-400"}`} />
                    {ns.indexed ? "Indexed" : "Missing"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title={`Chunks (${status.chunks.length})`}>
        <div className="space-y-3">
          {status.chunks.map((c) => {
            const open = openChunk === c.chunk_id;
            return (
              <div key={c.chunk_id} className="border border-gray-800 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenChunk(open ? null : c.chunk_id)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-gray-900 hover:bg-gray-800/70 transition-colors text-left"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <svg className={`w-3.5 h-3.5 shrink-0 text-gray-500 transition-transform ${open ? "rotate-90" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    <span className="text-xs font-mono text-gray-300 truncate">{c.chunk_id}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.self_score != null && (
                      <span className={`text-xs font-semibold ${scoreColor(c.self_score)}`}>
                        score {c.self_score.toFixed(4)}
                      </span>
                    )}
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${c.indexed ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${c.indexed ? "bg-green-400" : "bg-red-400"}`} />
                      {c.indexed ? "Indexed" : "Missing"}
                    </span>
                  </div>
                </button>

                {open && (
                  <div className="p-3 space-y-3 bg-gray-950/50">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Embedded content (what was turned into a vector)</p>
                      <p className="text-xs text-gray-300 whitespace-pre-wrap break-words bg-gray-900 border border-gray-800 rounded-lg p-2.5 max-h-40 overflow-auto">
                        {c.content_preview}
                      </p>
                    </div>

                    {c.indexed && (
                      <>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                          <MetaField label="Vector dimension" value={c.vector_dimension ? String(c.vector_dimension) : undefined} />
                          <MetaField label="Self-similarity" value={c.self_score != null ? c.self_score.toFixed(4) : undefined} />
                        </div>

                        {c.vector_preview && c.vector_preview.length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                              Vector preview (first {c.vector_preview.length} of {c.vector_dimension})
                            </p>
                            <p className="text-[11px] font-mono text-gray-400 break-all bg-gray-900 border border-gray-800 rounded-lg p-2">
                              [{c.vector_preview.map((v) => v.toFixed(4)).join(", ")}, …]
                            </p>
                          </div>
                        )}

                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Stored metadata</p>
                          <pre className="text-[11px] text-gray-300 bg-gray-900 border border-gray-800 rounded-lg p-2.5 overflow-auto max-h-56 whitespace-pre-wrap">
                            {JSON.stringify(c.stored_metadata ?? {}, null, 2)}
                          </pre>
                        </div>

                        {c.nearest_neighbors && c.nearest_neighbors.length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Nearest neighbors in this namespace</p>
                            <div className="space-y-1">
                              {c.nearest_neighbors.map((n) => (
                                <div key={n.id} className="flex items-center justify-between gap-2 text-xs bg-gray-900 border border-gray-800 rounded-lg px-2.5 py-1.5">
                                  <span className="font-mono text-gray-400 truncate">{n.metadata?.title || n.id}</span>
                                  <span className={`font-semibold shrink-0 ${scoreColor(n.score)}`}>{n.score.toFixed(4)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {!c.indexed && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Expected metadata (not yet in Pinecone)</p>
                        <pre className="text-[11px] text-gray-300 bg-gray-900 border border-gray-800 rounded-lg p-2.5 overflow-auto max-h-56 whitespace-pre-wrap">
                          {JSON.stringify(c.expected_metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
};

const SELLER_POPOVER_WIDTH = 288; // w-72

const SellerPopover: React.FC<{
  state?: { loading: boolean; data: any | null; error: boolean };
  fallbackName?: string;
  anchorRect: DOMRect;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}> = ({ state, fallbackName, anchorRect, onMouseEnter, onMouseLeave }) => {
  const u = state?.data;
  const avatar = u?.profile_picture || u?.avatar_url || u?.photo_url;
  const company = u?.agency_name || u?.company_name || u?.business_name;
  const tier = u?.subscription_tier || u?.subscription?.tier || u?.plan;

  // Rendered in a portal (not nested in the table's overflow-x-auto wrapper,
  // which clips vertical overflow too), so it needs its own viewport-relative
  // position instead of `absolute` positioning against the trigger.
  const left = Math.min(anchorRect.left, window.innerWidth - SELLER_POPOVER_WIDTH - 16);
  const top = anchorRect.bottom + 8;

  return createPortal(
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ position: "fixed", top, left, width: SELLER_POPOVER_WIDTH }}
      className="z-50 bg-gray-900 border border-gray-800 rounded-xl shadow-2xl p-4 text-left cursor-default"
    >
      {state?.loading && <p className="text-xs text-gray-500">Loading seller…</p>}
      {state?.error && <p className="text-xs text-red-400">Failed to load seller.</p>}
      {!state?.loading && !state?.error && !u && <p className="text-xs text-gray-500">No details.</p>}
      {u && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            {avatar ? (
              <img src={avatar} alt="" className="w-10 h-10 rounded-full object-cover border border-gray-700" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-indigo-500/20 text-indigo-300 flex items-center justify-center text-sm font-semibold">
                {(u.name || fallbackName || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{u.name || fallbackName || "—"}</p>
              {u.role && <p className="text-[11px] text-gray-500 capitalize">{u.role}</p>}
            </div>
          </div>
          <div className="space-y-1.5">
            {u.email && <PopRow label="Email" value={u.email} />}
            {(u.phone || u.whatsapp_number) && <PopRow label="Phone" value={u.phone || u.whatsapp_number} />}
            {company && <PopRow label="Company" value={company} />}
            {(u.country || u.city) && <PopRow label="Location" value={[u.city, u.country].filter(Boolean).join(", ")} />}
            {tier && <PopRow label="Tier" value={String(tier)} />}
            <PopRow label="Verified" value={u.is_verified ? "Yes" : "No"} />
            {u.created_at && <PopRow label="Joined" value={formatIST(u.created_at)} />}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

const PopRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between gap-3 text-xs">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="text-gray-300 text-right break-all">{value}</span>
  </div>
);

const AdminDealsPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const { alertModal, showAlert } = useAlertModal();
  const { showError } = useAdminToast();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<boolean | null>(null);
  const [selectedTravelStatus, setSelectedTravelStatus] = useState<"stale" | "upcoming" | "no_date" | null>(null);
  const [noPriceOnly, setNoPriceOnly] = useState(false);
  const [missingMediaOnly, setMissingMediaOnly] = useState(false);
  // "Broken S3 refs": at least one stored key points at an object that no
  // longer exists. Distinct from "missing" (which is "no media at all") and
  // deliberately also sweeps the legacy shadow `deals` collection.
  const [brokenMediaOnly, setBrokenMediaOnly] = useState(false);
  // "Video only" is NOT a defect — video_url alone is valid media, same as
  // image_clean alone. This is a tracking view for teams that want to
  // backfill a static poster photo on top of an existing video anyway.
  const [videoOnly, setVideoOnly] = useState(false);
  // Deals blocked from publishing because their entire extracted travel-date
  // window has already passed (admin_review.flagged_reason === "dates_expired").
  // See /api/admin/date-expiry-deals for the approve/reject actions.
  const [dateReviewOnly, setDateReviewOnly] = useState(false);
  // Pinecone indexing status filter — "full"/"partial"/"none" (a deal can be
  // indexed into a primary namespace + secondary cross-category namespaces;
  // "partial" means only some of those namespaces actually have a vector).
  // Live-scanned + cached on the backend (see admin_repo.list_deals /
  // reconcile.compute_pinecone_index_status), not a stored Mongo field.
  const [pineconeStatusFilter, setPineconeStatusFilter] = useState<"full" | "partial" | "none" | null>(null);
  const [viewMode, setViewMode] = useState<"typed" | "stuck_shadow">("typed");
  const [selectedDeal, setSelectedDeal] = useState<any | null>(null);
  const [stuckShadowDetail, setStuckShadowDetail] = useState<import("../../features/admin/types").StuckShadowDealDetail | null>(null);
  const [stuckShadowLoading, setStuckShadowLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  // Seller details cache + hover popover state
  const [sellerCache, setSellerCache] = useState<Record<string, { loading: boolean; data: any | null; error: boolean }>>({});
  // Key is "dealId:dealerId" to avoid showing popover on ALL rows with the same seller
  const [hoveredSeller, setHoveredSeller] = useState<{ rowKey: string; dealerId: string; name: string; anchorRect: DOMRect } | null>(null);
  const [rawOpen, setRawOpen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<{ url: string; label: string; isVideo?: boolean } | null>(null);
  // URLs that failed to load (dead S3 key — e.g. bucket migrated/suspended) so we can flag them instead of showing a silent broken image/video.
  const [brokenMediaUrls, setBrokenMediaUrls] = useState<Set<string>>(new Set());
  const [detailTab, setDetailTab] = useState<"details" | "dates" | "pinecone">("details");
  const [pineconeStatus, setPineconeStatus] = useState<import("../../features/admin/types").DealPineconeStatus | null>(null);
  const [pineconeLoading, setPineconeLoading] = useState(false);
  const [pineconeError, setPineconeError] = useState<string | null>(null);
  const [pineconeReindexing, setPineconeReindexing] = useState(false);
  const [pineconeReindexNotice, setPineconeReindexNotice] = useState<string | null>(null);
  const hoverHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchParams] = useSearchParams();
  const [deepLinkError, setDeepLinkError] = useState("");
  useEffect(() => { if (deepLinkError) showError(deepLinkError); }, [deepLinkError, showError]);
  const [unpublishBusy, setUnpublishBusy] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [rowActionBusy, setRowActionBusy] = useState<Set<string>>(new Set());
  const [publishError, setPublishError] = useState<string | null>(null);
  useEffect(() => { if (publishError) showError(publishError); }, [publishError, showError]);
  const [publishErrorDetail, setPublishErrorDetail] = useState<{
    code?: string;
    message: string;
    onboarding_step_key?: string;
    pending_countries?: string[];
  } | null>(null);
  const [publishNotice, setPublishNotice] = useState<string | null>(null);
  const [boostForm, setBoostForm] = useState({ admin_likes_boost: "0", admin_saves_boost: "0" });
  const [boostBusy, setBoostBusy] = useState(false);
  const [boostError, setBoostError] = useState<string | null>(null);
  const [boostSaved, setBoostSaved] = useState(false);
  const [rowPublishErrorModal, setRowPublishErrorModal] = useState<{
    code?: string;
    message: string;
    onboarding_step_key?: string;
    pending_countries?: string[];
    dealCollection?: string;
    dealId?: string;
    dealerId?: string;
    dealerName?: string;
  } | null>(null);
  // Deal ids with an in-flight "Generate AI Flyer" job, and per-deal error text.
  // Scoped per deal id so only the clicked row/modal shows a busy state — the
  // rest of the page stays fully interactive.
  const [flyerGenBusy, setFlyerGenBusy] = useState<Set<string>>(new Set());
  const [flyerGenError, setFlyerGenError] = useState<Record<string, string>>({});
  // Deal ids whose AI flyer finished generating successfully THIS session — used
  // only to highlight the row (green) on the "Missing image/video" filter so an
  // admin working through the backlog can see progress at a glance; cleared on
  // page reload/refetch since the row will have dropped out of that filter by then.
  const [flyerGenDone, setFlyerGenDone] = useState<Set<string>>(new Set());
  useBodyScrollLock(!!selectedDeal || !!previewMedia || !!rowPublishErrorModal);

  // Single-deal, click-triggered only: fires the generate endpoint, then polls
  // flyer_auto_status and patches just this deal's row (and the detail modal if
  // it happens to be showing the same deal) with the freshly fetched doc, so the
  // new flyer appears without a page reload or a full list refetch.
  const handleGenerateAiFlyer = async (collection: string, dealId: string) => {
    if (!collection || !dealId || flyerGenBusy.has(dealId)) return;
    setFlyerGenBusy((prev) => new Set(prev).add(dealId));
    setFlyerGenError((prev) => {
      const next = { ...prev };
      delete next[dealId];
      return next;
    });
    const clearBusy = () =>
      setFlyerGenBusy((prev) => {
        const next = new Set(prev);
        next.delete(dealId);
        return next;
      });
    const setErr = (msg: string) => setFlyerGenError((prev) => ({ ...prev, [dealId]: msg }));
    try {
      await adminService.generateAiFlyer(collection, dealId);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "Failed to start AI flyer generation.";
      setErr(msg);
      showAlert(msg);
      clearBusy();
      return;
    }
    await pollFlyerStatus(dealId, {
      collection,
      onTerminal: (freshDeal, status) => {
        if (status === "failed") {
          const msg =
            freshDeal?.flyer_auto_error ||
            "AI flyer generation failed — check backend logs (flyer_auto).";
          setErr(msg);
          showAlert(msg);
        } else if (freshDeal) {
          setData((prev) =>
            prev
              ? {
                  ...prev,
                  items: prev.items.map((it: any) =>
                    (it.id || it._id) === dealId ? { ...it, ...freshDeal } : it,
                  ),
                }
              : prev,
          );
          setSelectedDeal((prev: any) =>
            prev && (prev.id || prev._id) === dealId ? { ...prev, ...freshDeal } : prev,
          );
          setFlyerGenDone((prev) => new Set(prev).add(dealId));
        }
        clearBusy();
      },
      onTimeout: () => {
        setErr("Still generating in the background — reopen this deal in a minute to check.");
        clearBusy();
      },
    });
  };

  const handleRowAutoAddMarketAndPublish = async () => {
    const { dealCollection, dealId } = rowPublishErrorModal || {};
    if (!dealCollection || !dealId || rowActionBusy.has(dealId)) return;
    setRowActionBusy((prev) => new Set(prev).add(dealId));
    try {
      await adminService.publishDeal(dealCollection, dealId, { addMissingMarket: true });
      setData((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((it: any) =>
                (it.id || it._id) === dealId ? { ...it, lifecycle: { ...it.lifecycle, is_active: true } } : it,
              ),
            }
          : prev,
      );
      setRowPublishErrorModal(null);
      showAlert("Published. The missing market was added to this seller's approved markets automatically.", { variant: "info", title: "Published" });
    } catch (err2: any) {
      const detail2 = err2?.response?.data?.detail;
      const detail2Message = typeof detail2 === "string" ? detail2 : detail2?.message;
      setRowPublishErrorModal((prev) =>
        prev
          ? { ...prev, message: `Tried to auto-add the missing market, but publishing still failed${detail2Message ? `: ${detail2Message}` : "."}` }
          : prev,
      );
    } finally {
      setRowActionBusy((prev) => {
        const next = new Set(prev);
        next.delete(dealId);
        return next;
      });
    }
  };

  // Deep-link from the admin notification bell (e.g. a possible-duplicate
  // alert) — ?deal_id=X opens that specific deal's modal directly instead of
  // requiring the admin to find it in the paginated list.
  useEffect(() => {
    const dealId = searchParams.get("deal_id");
    if (!dealId) return;
    (async () => {
      try {
        const resolved = await dealsService.resolveCollection(dealId);
        if (!resolved) { setDeepLinkError("Could not find that deal — it may have been deleted."); return; }
        // Use the admin-scoped detail endpoint, not dealsService.getTypedDeal (the
        // public/buyer-facing route) — that one runs the response through
        // _finalize_public_response(), which moves `validity` under
        // `ai_extracted_raw` and strips other admin-only fields, so this modal
        // showed less than the same modal opened from the main Deals list.
        const deal = await adminService.getDealInCollection(resolved.collection, resolved.typedId);
        setSelectedDeal({ ...deal, id: resolved.typedId, collection_name: resolved.collection });
      } catch {
        setDeepLinkError("Could not load that deal.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSeller = useCallback((dealerId: string) => {
    if (!dealerId) return;
    setSellerCache((prev) => {
      if (prev[dealerId]) return prev; // already fetched or in-flight
      adminService
        .getUser(dealerId)
        .then((data) => setSellerCache((p) => ({ ...p, [dealerId]: { loading: false, data, error: false } })))
        .catch(() => setSellerCache((p) => ({ ...p, [dealerId]: { loading: false, data: null, error: true } })));
      return { ...prev, [dealerId]: { loading: true, data: null, error: false } };
    });
  }, []);

  const showSellerPopover = useCallback((rowKey: string, dealerId: string, name: string, anchorRect: DOMRect) => {
    if (hoverHideTimer.current) { clearTimeout(hoverHideTimer.current); hoverHideTimer.current = null; }
    if (!dealerId) return;
    setHoveredSeller({ rowKey, dealerId, name, anchorRect });
    fetchSeller(dealerId);
  }, [fetchSeller]);

  const hideSellerPopover = useCallback(() => {
    if (hoverHideTimer.current) clearTimeout(hoverHideTimer.current);
    hoverHideTimer.current = setTimeout(() => setHoveredSeller(null), 150);
  }, []);

  // Unified search state
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Deep-link from the admin Users page's "Active Deals" column — clicking a
  // seller's active/inactive count lands here pre-filtered to that seller
  // (by dealer_id, which works even for sellers with no email on file) and
  // the matching Active/Inactive status tab, instead of the admin having to
  // re-find the seller and re-apply the status filter by hand.
  const [dealerIdFilter, setDealerIdFilter] = useState<string | null>(null);
  useEffect(() => {
    const sellerSearch = searchParams.get("search");
    const dealerId = searchParams.get("dealer_id");
    const activeParam = searchParams.get("is_active");
    if (sellerSearch) { setSearchText(sellerSearch); setDebouncedSearch(sellerSearch); }
    if (dealerId) setDealerIdFilter(dealerId);
    if (activeParam === "true") setSelectedStatus(true);
    else if (activeParam === "false") setSelectedStatus(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Separate bulk deal-ID lookup — comma/whitespace-separated, independent of
  // the free-text search box above so pasting several IDs never gets treated
  // as a text query.
  const [dealIdsText, setDealIdsText] = useState("");
  const [debouncedDealIds, setDebouncedDealIds] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedDealIds(dealIdsText), 500);
    return () => clearTimeout(timer);
  }, [dealIdsText]);
  const [chips, setChips] = useState<FilterChip[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ tags: [], countries: [], cities: [], categories: [] });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Filter panel (categories/countries/cities/tags)
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterPanelSearches, setFilterPanelSearches] = useState<Record<string, string>>({ categories: "", countries: "", cities: "", tags: "" });

  // Date-range filters (created_at / updated_at)
  const [dateFilters, setDateFilters] = useState<DateFilterEntry[]>([]);

  // Load filter options once
  useEffect(() => {
    dealsService.getFilterOptions().then((opts: any) => setFilterOptions(opts)).catch(() => {});
  }, []);

  // Only actually-filled company/brand/agency names — not every seller has
  // one, so this list (not free text) is what "group by company" filters on.
  const [companyNames, setCompanyNames] = useState<string[]>([]);
  useEffect(() => {
    adminService.getCompanyNames().then(setCompanyNames).catch(() => {});
  }, []);
  const selectedCompany = chips.find((c) => c.type === "company")?.value || "";
  const setSelectedCompany = (value: string) => {
    setChips((prev) => {
      const withoutCompany = prev.filter((c) => c.type !== "company");
      return value ? [...withoutCompany, { type: "company", value }] : withoutCompany;
    });
    setPage(1);
  };

  // Reset the detail modal to "Details" and drop stale Pinecone data whenever
  // a different deal is opened (or the modal closes).
  useEffect(() => {
    setDetailTab("details");
    setPineconeStatus(null);
    setPineconeError(null);
    setPineconeReindexNotice(null);
    setBoostForm({
      admin_likes_boost: String(selectedDeal?.admin_likes_boost ?? 0),
      admin_saves_boost: String(selectedDeal?.admin_saves_boost ?? 0),
    });
    setBoostError(null);
    setBoostSaved(false);
  }, [selectedDeal?.id, selectedDeal?._id]);

  // Fetch Pinecone status lazily — only once the admin actually opens that tab.
  useEffect(() => {
    if (detailTab !== "pinecone" || !selectedDeal || viewMode !== "typed") return;
    const dealCollection = selectedDeal.collection_name || selectedDeal.collection;
    const dealId = selectedDeal.id || selectedDeal._id;
    if (!dealCollection || !dealId) return;
    setPineconeLoading(true);
    setPineconeError(null);
    adminService
      .getDealPineconeStatus(dealCollection, dealId)
      .then(setPineconeStatus)
      .catch((err: any) => setPineconeError(err?.response?.data?.detail || "Failed to load Pinecone status."))
      .finally(() => setPineconeLoading(false));
  }, [detailTab, selectedDeal, viewMode]);

  const handleReindexPinecone = async () => {
    if (!selectedDeal || pineconeReindexing) return;
    const dealCollection = selectedDeal.collection_name || selectedDeal.collection;
    const dealId = selectedDeal.id || selectedDeal._id;
    if (!dealCollection || !dealId) return;
    setPineconeReindexing(true);
    setPineconeReindexNotice(null);
    try {
      const result = await adminService.reindexDealPinecone(dealCollection, dealId);
      setPineconeReindexNotice(
        result.success ? "Reindexed successfully." : "Reindex ran but reported failure — check backend logs.",
      );
      const fresh = await adminService.getDealPineconeStatus(dealCollection, dealId);
      setPineconeStatus(fresh);
    } catch (err: any) {
      setPineconeReindexNotice(err?.response?.data?.detail || "Reindex failed.");
    } finally {
      setPineconeReindexing(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Build dropdown suggestions from filter options filtered by searchText
  const q = searchText.toLowerCase().trim();
  const existingValues = new Set(chips.map((c) => `${c.type}:${c.value}`));

  const suggestions: FilterChip[] = [];
  if (q.length >= 1) {
    for (const v of filterOptions.countries) {
      if (v.toLowerCase().includes(q) && !existingValues.has(`country:${v}`)) suggestions.push({ type: "country", value: v });
    }
    for (const v of filterOptions.cities) {
      if (v.toLowerCase().includes(q) && !existingValues.has(`city:${v}`)) suggestions.push({ type: "city", value: v });
    }
    for (const v of filterOptions.categories) {
      if (v.toLowerCase().includes(q) && !existingValues.has(`category:${v}`)) suggestions.push({ type: "category", value: v });
    }
    for (const v of filterOptions.tags) {
      if (v.toLowerCase().includes(q) && !existingValues.has(`tag:${v}`)) suggestions.push({ type: "tag", value: v });
    }
  }

  const addChip = (chip: FilterChip) => {
    setChips((prev) => [...prev, chip]);
    setSearchText("");
    setDropdownOpen(false);
    setPage(1);
  };

  const removeChip = (idx: number) => {
    setChips((prev) => prev.filter((_, i) => i !== idx));
    setPage(1);
  };

  const toggleFilterChip = (type: FilterChip["type"], value: string) => {
    setChips((prev) => {
      const exists = prev.some((c) => c.type === type && c.value === value);
      if (exists) return prev.filter((c) => !(c.type === type && c.value === value));
      return [...prev, { type, value }];
    });
    setPage(1);
  };

  const totalChips = chips.length;

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      if (viewMode === "stuck_shadow") {
        const params: Record<string, any> = { page, page_size: pageSize };
        if (searchText.trim()) params.search = searchText.trim();
        setData(await adminService.getStuckShadowDeals(params));
        return;
      }
      const params: Record<string, any> = { page, page_size: pageSize };
      if (selectedCollection) params.collection_name = selectedCollection;
      if (selectedStatus !== null) params.is_active = selectedStatus;
      if (dealerIdFilter) params.dealer_id = dealerIdFilter;
      if (selectedTravelStatus !== null) params.travel_status = selectedTravelStatus;
      if (noPriceOnly) params.no_price = true;
      if (missingMediaOnly) params.missing_media = true;
      if (brokenMediaOnly) params.broken_media = true;
      if (videoOnly) params.video_only = true;
      if (dateReviewOnly) params.needs_date_review = true;
      if (pineconeStatusFilter) params.pinecone_status = pineconeStatusFilter;
      const countries = chips.filter((c) => c.type === "country").map((c) => c.value);
      const cities = chips.filter((c) => c.type === "city").map((c) => c.value);
      const categories = chips.filter((c) => c.type === "category").map((c) => c.value);
      const tags = chips.filter((c) => c.type === "tag").map((c) => c.value);
      const company = chips.find((c) => c.type === "company")?.value;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim().toLowerCase();
      if (debouncedDealIds.trim()) params.deal_ids = debouncedDealIds.trim();
      if (countries.length) params.countries = countries.join(",");
      if (cities.length) params.cities = cities.join(",");
      if (categories.length) params.categories = categories.join(",");
      if (tags.length) params.tags = tags.join(",");
      if (company) params.company_name = company;
      const activeDates = dateFilters.filter((d) => d.from || d.to);
      if (activeDates.length) params.date_filters = JSON.stringify(activeDates);
      setData(await adminService.getDeals(params));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, selectedCollection, selectedStatus, selectedTravelStatus, noPriceOnly, missingMediaOnly, brokenMediaOnly, videoOnly, dateReviewOnly, pineconeStatusFilter, chips, debouncedSearch, debouncedDealIds, dateFilters, viewMode, dealerIdFilter]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);
  useEffect(() => { setPage(1); }, [pageSize]);

  const openDeal = async (deal: any) => {
    setPublishError(null);
    setPublishErrorDetail(null);
    setPublishNotice(null);
    if (viewMode === "stuck_shadow") {
      setSelectedDeal(deal);
      setStuckShadowDetail(null);
      setStuckShadowLoading(true);
      try {
        setStuckShadowDetail(await adminService.getStuckShadowDeal(deal.id || deal._id));
      } catch (err) {
        console.error(err);
      } finally {
        setStuckShadowLoading(false);
      }
      return;
    }
    setSelectedDeal(deal);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminService.deleteDeal(deleteTarget.id);
      setDeleteTarget(null);
      fetchDeals();
    } catch (err) {
      console.error(err);
    }
  };

  const typeColor = (type: string) => {
    const map: Record<string, string> = {
      DEAL: "bg-amber-500/10 text-amber-400",
      PACKAGE: "bg-violet-500/10 text-violet-400",
      FLIGHT: "bg-sky-500/10 text-sky-400",
      HOTEL: "bg-rose-500/10 text-rose-400",
      CRUISE: "bg-cyan-500/10 text-cyan-400",
      TRANSFER: "bg-emerald-500/10 text-emerald-400",
      SERVICE: "bg-orange-500/10 text-orange-400",
      EXPERIENCE: "bg-fuchsia-500/10 text-fuchsia-400",
    };
    return map[type] || "bg-gray-500/10 text-gray-400";
  };

  return (
    <>
    <div>
      <AdminPageHeader
        title="Deals"
        subtitle={
          viewMode === "stuck_shadow"
            ? "Deals stuck after failed AI analysis — not counted in the main deals total"
            : "Manage all marketplace deals & inventory"
        }
        meta={`${data?.total ?? 0} total`}
        actions={<RefreshButton onRefresh={fetchDeals} />}
      />

      {deepLinkError && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {deepLinkError}
        </div>
      )}

      {/* Unified search + chips */}
      <div className="mb-4" ref={searchRef}>
        <div className="flex flex-wrap gap-2 items-center bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 min-h-[44px] focus-within:ring-2 focus-within:ring-indigo-500/50 relative">
          {dealerIdFilter && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border border-indigo-500/40 bg-indigo-500/10 text-indigo-300">
              <span className="opacity-60 text-[10px] uppercase tracking-wide">Seller</span>
              {dealerIdFilter}
              <button onClick={() => setDealerIdFilter(null)} className="ml-0.5 hover:opacity-70 transition-opacity">
                <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              </button>
            </span>
          )}
          {chips.map((chip, idx) => (
            <span key={idx} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border ${CHIP_COLORS[chip.type]}`}>
              <span className="opacity-60 text-[10px] uppercase tracking-wide">{CHIP_LABEL[chip.type]}</span>
              {chip.value}
              <button onClick={() => removeChip(idx)} className="ml-0.5 hover:opacity-70 transition-opacity">
                <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              </button>
            </span>
          ))}
          <input
            type="text"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setDropdownOpen(true); }}
            onFocus={() => { if (searchText.length >= 1) setDropdownOpen(true); }}
            placeholder={chips.length === 0 ? "Search by title, dealer name/email, country, city, category, tag…" : "Add more filters…"}
            className="flex-1 min-w-[180px] bg-transparent text-white placeholder-gray-500 focus:outline-none text-sm py-0.5"
          />
          {chips.length > 0 && (
            <button onClick={() => { setChips([]); setPage(1); }} className="text-xs text-gray-500 hover:text-red-400 transition-colors shrink-0">Clear all</button>
          )}

          {/* Dropdown */}
          {dropdownOpen && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-800 rounded-xl shadow-2xl z-50 max-h-72 overflow-y-auto">
              {(["country", "city", "category", "tag"] as Array<FilterChip["type"]>).map((type) => {
                const group = suggestions.filter((s) => s.type === type);
                if (!group.length) return null;
                return (
                  <div key={type}>
                    <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-gray-500 font-medium">{CHIP_LABEL[type]}</div>
                    <div className="flex flex-wrap gap-1.5 px-3 pb-2">
                      {group.slice(0, 12).map((s) => (
                        <button key={s.value} onClick={() => addChip(s)}
                          className={`text-xs px-2 py-1 rounded-full border transition-colors hover:brightness-110 ${CHIP_COLORS[s.type]}`}>
                          {s.value}
                        </button>
                      ))}
                      {group.length > 12 && <span className="text-xs text-gray-500 py-1">+{group.length - 12} more</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Bulk deal-ID lookup — separate from the search box above, for pasting one or more deal IDs at once */}
      <div className="mb-4 flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 min-h-[44px] focus-within:ring-2 focus-within:ring-indigo-500/50">
        <svg className="w-4 h-4 text-gray-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6M9 8h1m5 12H7a2 2 0 01-2-2V6a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V18a2 2 0 01-2 2z"/></svg>
        <input
          type="text"
          value={dealIdsText}
          onChange={(e) => { setDealIdsText(e.target.value); setPage(1); }}
          placeholder="Search by deal ID(s) — paste one or many, separated by commas/spaces/newlines"
          className="flex-1 bg-transparent text-white placeholder-gray-500 focus:outline-none text-sm py-0.5"
        />
        {dealIdsText.trim() && (
          <button onClick={() => setDealIdsText("")} className="text-xs text-gray-500 hover:text-red-400 transition-colors shrink-0">Clear</button>
        )}
      </div>

      {/* Filters toggle */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setFilterPanelOpen((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
            filterPanelOpen || totalChips > 0
              ? "bg-indigo-600 text-white border-indigo-600"
              : "bg-gray-900 text-gray-400 border-gray-800 hover:bg-gray-800"
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
          </svg>
          Filters
          {totalChips > 0 && (
            <span className="ml-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-white text-indigo-600 text-xs font-bold">
              {totalChips}
            </span>
          )}
        </button>

        <select
          value={selectedCompany}
          onChange={(e) => setSelectedCompany(e.target.value)}
          className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
        >
          <option value="">All Companies</option>
          {companyNames.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <DateRangeFilter
          fields={DEAL_DATE_FIELDS}
          value={dateFilters}
          onChange={(v) => { setDateFilters(v); setPage(1); }}
        />
      </div>

      {/* Filter Panel */}
      {filterPanelOpen && (
        <div className="mb-5 bg-gray-900 border border-gray-800 rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
            <p className="text-sm font-semibold text-white">Filter Deals</p>
            <div className="flex items-center gap-3">
              {totalChips > 0 && (
                <button onClick={() => { setChips([]); setPage(1); }} className="text-xs text-red-400 hover:text-red-300 font-medium">Clear all</button>
              )}
              <button onClick={() => setFilterPanelOpen(false)} className="text-gray-500 hover:text-gray-300 text-lg leading-none">×</button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 divide-y sm:divide-y-0 sm:divide-x divide-gray-800">
            {FILTER_SECTIONS.map(({ key, type, label }) => {
              const opts = filterOptions[key] ?? [];
              const search = filterPanelSearches[key] ?? "";
              const filtered = opts.filter((o) => o.toLowerCase().includes(search.toLowerCase()));
              return (
                <div key={key} className="p-3 min-w-0">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{label}</p>
                  <input
                    type="text"
                    placeholder={`Search ${label.toLowerCase()}…`}
                    value={search}
                    onChange={(e) => setFilterPanelSearches((prev) => ({ ...prev, [key]: e.target.value }))}
                    className="w-full text-xs px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg mb-2 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <div className="space-y-1 max-h-36 overflow-y-auto">
                    {filtered.length === 0 && (
                      <p className="text-xs text-gray-600 py-1">No options</p>
                    )}
                    {filtered.map((opt) => (
                      <label key={opt} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={chips.some((c) => c.type === type && c.value === opt)}
                          onChange={() => toggleFilterChip(type, opt)}
                          className="w-3.5 h-3.5 rounded border-gray-600 bg-gray-800 text-indigo-500 focus:ring-indigo-500"
                        />
                        <span className="text-xs text-gray-300 group-hover:text-indigo-400 truncate">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Collection filter toggle */}
      <div className="flex gap-2 flex-wrap mb-3">
        {TYPE_FILTERS.map(({ label, collectionName }) => (
          <button
            key={label}
            onClick={() => { setSelectedCollection(collectionName); setViewMode("typed"); setPage(1); }}
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              viewMode === "typed" && selectedCollection === collectionName ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
            }`}
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => { setViewMode("stuck_shadow"); setSelectedCollection(null); setPage(1); }}
          className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
            viewMode === "stuck_shadow" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
          }`}
        >
          Stuck / Shadow
        </button>
      </div>

      {/* Status (lifecycle) filter toggle — has its own total, separate from "All" */}
      <div className="flex gap-2 flex-wrap mb-3">
        {STATUS_FILTERS.map(({ label, isActive }) => (
          <button
            key={label}
            onClick={() => { setSelectedStatus(isActive); setPage(1); }}
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              selectedStatus === isActive
                ? isActive === false
                  ? "bg-red-500/20 text-red-400 border border-red-500/30"
                  : "bg-green-500/20 text-green-400 border border-green-500/30"
                : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Travel-date staleness filter — independent of active/published state;
          a deal can be Inactive AND Stale, or Active AND Stale, at once.
          "No Date Present" is its own bucket, never mixed into Stale/Upcoming. */}
      <div className="flex gap-2 flex-wrap mb-3">
        {TRAVEL_STATUS_FILTERS.map(({ label, travelStatus }) => (
          <button
            key={label}
            onClick={() => { setSelectedTravelStatus(travelStatus); setPage(1); }}
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              selectedTravelStatus === travelStatus
                ? travelStatus === "stale"
                  ? "bg-red-500/20 text-red-400 border border-red-500/30"
                  : travelStatus === "no_date"
                    ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                    : "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30"
                : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* No-price filter — a deal can have no usable price anywhere
          (no headline price, no per-tier/cabin/room/pax price).
          Missing image/video — computed on read: neither a usable image nor a
          video survives media resolution (covers deals whose S3 objects are gone). */}
      <div className="flex gap-2 flex-wrap mb-6">
        <button
          onClick={() => { setNoPriceOnly((v) => !v); setPage(1); }}
          className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
            noPriceOnly
              ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
              : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
          }`}
        >
          No Price
        </button>
        <button
          onClick={() => { setMissingMediaOnly((v) => !v); setPage(1); }}
          className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
            missingMediaOnly
              ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
              : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
          }`}
        >
          Missing image/video
        </button>
        <button
          onClick={() => { setBrokenMediaOnly((v) => !v); setPage(1); }}
          title="Deals with at least one stored S3 key whose object is gone — includes the legacy shadow collection"
          className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
            brokenMediaOnly
              ? "bg-red-500/20 text-red-400 border border-red-500/30"
              : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
          }`}
        >
          Broken S3 refs
        </button>
        <button
          onClick={() => { setVideoOnly((v) => !v); setPage(1); }}
          title="Not a defect — video_url alone is valid media, same as image_clean alone. Tracking view only, for teams that want to backfill a static poster photo on top of an existing video."
          className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
            videoOnly
              ? "bg-sky-500/20 text-sky-400 border border-sky-500/30"
              : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
          }`}
        >
          Video only (no poster)
        </button>
        <button
          onClick={() => { setDateReviewOnly((v) => !v); setPage(1); }}
          title="Deals blocked from publishing because their entire extracted travel-date window has already passed"
          className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
            dateReviewOnly
              ? "bg-red-500/20 text-red-400 border border-red-500/30"
              : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
          }`}
        >
          Admin Reviewable (Expired Dates)
        </button>
        {(["partial", "none"] as const).map((status) => (
          <button
            key={status}
            onClick={() => { setPineconeStatusFilter((v) => (v === status ? null : status)); setPage(1); }}
            title={
              status === "partial"
                ? "Indexed into some but not all of its expected Pinecone namespaces (primary + secondary categories)"
                : "Not indexed into any of its expected Pinecone namespaces — live-checked against Pinecone"
            }
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              pineconeStatusFilter === status
                ? "bg-red-500/20 text-red-400 border border-red-500/30"
                : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
            }`}
          >
            Pinecone: {status === "partial" ? "Partially indexed" : "Not indexed"}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/80">
                <th className="text-left py-3 px-4 text-gray-500 font-medium whitespace-nowrap">Title</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium whitespace-nowrap">Type</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium whitespace-nowrap">Category</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium whitespace-nowrap">Destination</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium whitespace-nowrap">Tags</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium whitespace-nowrap">Price</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium whitespace-nowrap">Seller</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium whitespace-nowrap">Status</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium whitespace-nowrap">Pinecone</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium whitespace-nowrap">Last Travel Date</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium whitespace-nowrap">Created</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="py-20 text-center text-gray-500"><Spinner /></td></tr>
              ) : !data?.items.length ? (
                <tr><td colSpan={12} className="py-20 text-center text-gray-500">No deals found</td></tr>
              ) : (
                data.items.map((deal, rowIdx) => {
                  const countries: string[] = deal.destination?.countries ?? [];
                  const cities: string[] = deal.destination?.cities ?? [];
                  const tags: string[] = deal.tags ?? [];
                  const isActive = deal.lifecycle?.is_active === true;
                  const price = deal.pricing?.discounted_price || deal.pricing?.original_price;
                  const currency = deal.pricing?.currency || "INR";
                  const rowCollection = deal.collection_name || deal.collection;
                  const rowDealId = deal.id || deal._id;
                  const rowBusy = !!rowDealId && rowActionBusy.has(rowDealId);
                  const applyRowLifecycle = (id: string, active: boolean) => {
                    setData((prev) =>
                      prev
                        ? {
                            ...prev,
                            items: prev.items.map((it: any) =>
                              (it.id || it._id) === id
                                ? { ...it, lifecycle: { ...it.lifecycle, is_active: active } }
                                : it,
                            ),
                          }
                        : prev,
                    );
                  };
                  const handleRowToggle = async (activate: boolean) => {
                    if (!rowCollection || !rowDealId || rowActionBusy.has(rowDealId)) return;
                    setRowActionBusy((prev) => new Set(prev).add(rowDealId));
                    const dealerId = deal.dealer_id;
                    const dealerName = deal.dealer_name || deal.seller_name;
                    try {
                      if (activate) {
                        await adminService.publishDeal(rowCollection, rowDealId);
                      } else {
                        await adminService.unpublishDeal(rowCollection, rowDealId);
                      }
                      applyRowLifecycle(rowDealId, activate);
                    } catch (err: any) {
                      console.error(err);
                      if (!activate) {
                        showAlert("Failed to unpublish deal.");
                      } else {
                        // Seller never declared this market — let the admin choose to
                        // auto-add it or go add it manually, instead of silently
                        // granting it on their behalf.
                        const detail = err?.response?.data?.detail;
                        if (typeof detail === "string") {
                          setRowPublishErrorModal({ message: detail, dealCollection: rowCollection, dealId: rowDealId, dealerId, dealerName });
                        } else if (detail && typeof detail === "object" && typeof detail.message === "string") {
                          setRowPublishErrorModal({ ...detail, dealCollection: rowCollection, dealId: rowDealId, dealerId, dealerName });
                        } else {
                          setRowPublishErrorModal({ message: "Failed to publish deal.", dealCollection: rowCollection, dealId: rowDealId, dealerId, dealerName });
                        }
                      }
                    } finally {
                      setRowActionBusy((prev) => {
                        const next = new Set(prev);
                        next.delete(rowDealId);
                        return next;
                      });
                    }
                  };
                  const rowNeedsDateReview =
                    deal.admin_review?.status === "pending" && deal.admin_review?.flagged_reason === "dates_expired";
                  const handleDateExpiryApprove = async () => {
                    if (!rowCollection || !rowDealId || rowActionBusy.has(rowDealId)) return;
                    setRowActionBusy((prev) => new Set(prev).add(rowDealId));
                    try {
                      await adminService.approveDateExpiryDeal(rowCollection, rowDealId, true);
                      applyRowLifecycle(rowDealId, true);
                      await fetchDeals();
                    } catch (err: any) {
                      console.error(err);
                      showAlert(err?.response?.data?.detail?.message || "Failed to approve deal.");
                    } finally {
                      setRowActionBusy((prev) => {
                        const next = new Set(prev);
                        next.delete(rowDealId);
                        return next;
                      });
                    }
                  };
                  const handleDateExpiryReject = async () => {
                    if (!rowCollection || !rowDealId || rowActionBusy.has(rowDealId)) return;
                    const reason = window.prompt("Reason for rejecting this deal (shown to the seller):");
                    if (!reason || !reason.trim()) return;
                    setRowActionBusy((prev) => new Set(prev).add(rowDealId));
                    try {
                      await adminService.rejectDateExpiryDeal(rowCollection, rowDealId, reason.trim());
                      await fetchDeals();
                    } catch (err: any) {
                      console.error(err);
                      showAlert(err?.response?.data?.detail?.message || "Failed to reject deal.");
                    } finally {
                      setRowActionBusy((prev) => {
                        const next = new Set(prev);
                        next.delete(rowDealId);
                        return next;
                      });
                    }
                  };
                  // Row highlight, scoped to the "Missing image/video" backlog view only —
                  // yellow while this deal's AI flyer is generating, green once it lands
                  // (this-session only; a filter refetch drops the row once it no longer
                  // qualifies as missing media, same as any other filtered row would).
                  const flyerRowClass = !missingMediaOnly
                    ? "hover:bg-gray-800/20"
                    : flyerGenBusy.has(rowDealId)
                      ? "bg-amber-500/10 hover:bg-amber-500/15"
                      : flyerGenDone.has(rowDealId)
                        ? "bg-emerald-500/10 hover:bg-emerald-500/15"
                        : "hover:bg-gray-800/20";
                  return (
                    <tr key={deal.id} className={`border-b border-gray-800/50 transition-colors align-top ${flyerRowClass}`}>
                      {/* Title */}
                      <td className="py-3 px-4 max-w-[200px] w-[200px]">
                        <p className="text-white font-medium truncate">{deal.title || "Untitled"}</p>
                        {deal.subtitle && <p className="text-gray-500 text-xs truncate mt-0.5">{deal.subtitle}</p>}
                      </td>
                      {/* Type */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-md text-xs font-medium ${typeColor(deal.item_type)}`}>
                          {deal.item_type || "—"}
                        </span>
                        {deal.collection_name && (
                          <p className="text-[10px] text-gray-500 font-mono mt-0.5">{deal.collection_name}</p>
                        )}
                      </td>
                      {/* Category */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className="text-amber-400 text-xs font-mono bg-amber-500/10 px-2 py-1 rounded-md">
                          {deal.categories?.primary?.slug || deal.category_slug || "—"}
                        </span>
                      </td>
                      {/* Destination */}
                      <td className="py-3 px-4 max-w-[160px] w-[160px]">
                        {countries.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-1">
                            {countries.slice(0, 2).map((c) => (
                              <span key={c} className="text-[10px] text-sky-300 bg-sky-500/10 px-1.5 py-0.5 rounded">{c}</span>
                            ))}
                            {countries.length > 2 && <span className="text-[10px] text-gray-500">+{countries.length - 2}</span>}
                          </div>
                        )}
                        {cities.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {cities.slice(0, 3).map((c) => (
                              <span key={c} className="text-[10px] text-violet-300 bg-violet-500/10 px-1.5 py-0.5 rounded">{c}</span>
                            ))}
                            {cities.length > 3 && <span className="text-[10px] text-gray-500">+{cities.length - 3}</span>}
                          </div>
                        )}
                        {!countries.length && !cities.length && <span className="text-gray-600 text-xs">—</span>}
                      </td>
                      {/* Tags */}
                      <td className="py-3 px-4 max-w-[180px] w-[180px]">
                        <div className="flex flex-wrap gap-1">
                          {tags.slice(0, 3).map((t) => (
                            <span key={t} className="text-[10px] text-emerald-300 bg-emerald-500/10 px-1.5 py-0.5 rounded">{t}</span>
                          ))}
                          {tags.length > 3 && <span className="text-[10px] text-gray-500">+{tags.length - 3}</span>}
                          {!tags.length && <span className="text-gray-600 text-xs">—</span>}
                        </div>
                      </td>
                      {/* Price */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        {price ? (
                          <div>
                            <p className="text-white font-medium">{currency} {Number(price).toLocaleString()}</p>
                            {deal.pricing?.original_price && deal.pricing?.discounted_price && deal.pricing.original_price > deal.pricing.discounted_price && (
                              <p className="text-gray-500 text-xs line-through">{currency} {Number(deal.pricing.original_price).toLocaleString()}</p>
                            )}
                          </div>
                        ) : <span className="text-gray-600 text-xs">—</span>}
                      </td>
                      {/* Seller */}
                      <td className="py-3 px-4 max-w-[140px] w-[140px] overflow-hidden">
                        {deal.dealer_id ? (() => {
                          const rowKey = `${rowIdx}:${deal.id}:${deal.dealer_id}`;
                          return (
                            <span
                              className="relative block max-w-full"
                              onMouseEnter={(e) => showSellerPopover(rowKey, deal.dealer_id, deal.dealer_name || "Seller", e.currentTarget.getBoundingClientRect())}
                              onMouseLeave={hideSellerPopover}
                            >
                              <span className="text-gray-200 text-xs truncate border-b border-dotted border-gray-600 cursor-help hover:text-indigo-300 transition-colors block max-w-full">
                                {deal.dealer_name || deal.seller_name || "—"}
                              </span>
                              {hoveredSeller?.rowKey === rowKey && (
                                <SellerPopover
                                  state={sellerCache[deal.dealer_id]}
                                  fallbackName={deal.dealer_name || deal.seller_name}
                                  anchorRect={hoveredSeller.anchorRect}
                                  onMouseEnter={() => { if (hoverHideTimer.current) { clearTimeout(hoverHideTimer.current); hoverHideTimer.current = null; } }}
                                  onMouseLeave={hideSellerPopover}
                                />
                              )}
                            </span>
                          );
                        })() : (
                          <p className="text-gray-300 text-xs truncate">{deal.dealer_name || deal.seller_name || "—"}</p>
                        )}
                      </td>
                      {/* Status */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${isActive ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-green-400" : "bg-red-400"}`} />
                          {isActive ? "Active" : "Inactive"}
                        </span>
                        {rowNeedsDateReview && (
                          <span className="block mt-1 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-500/10 text-red-400">
                            Dates Expired — Review Needed
                          </span>
                        )}
                      </td>
                      {/* Pinecone indexing status — live-scanned + cached on the backend
                          (see list_deals / reconcile.compute_pinecone_index_status),
                          annotated on every row regardless of whether the filter above
                          is active, so an admin never has to check Pinecone separately. */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        {deal.pinecone_status === "full" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-green-500/10 text-green-400">
                            Fully indexed
                          </span>
                        ) : deal.pinecone_status === "partial" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-amber-500/10 text-amber-400">
                            Partially indexed
                          </span>
                        ) : deal.pinecone_status === "none" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-red-500/10 text-red-400">
                            Not indexed
                          </span>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      {/* Last Travel Date — purely date-derived from the deal's own
                          validity data (no AI), independent of Active/Inactive. */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        {deal.last_travel_date ? (
                          <div>
                            <p className={`text-xs ${deal.is_travel_stale ? "text-red-400" : "text-gray-300"}`}>
                              {formatIST(deal.last_travel_date)}
                            </p>
                            {deal.is_travel_stale && (
                              <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-500/10 text-red-400">
                                Crossed
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      {/* Created */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <p className="text-gray-500 text-xs">{deal.created_at ? formatIST(deal.created_at) : "—"}</p>
                      </td>
                      {/* Actions */}
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => openDeal(deal)} className="px-2 py-1 text-xs text-indigo-400 hover:bg-indigo-500/10 rounded-md transition-colors">View</button>
                          {rowDealId && (
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(`${env.webFrontendUrl}/deals?deal_id=${encodeURIComponent(rowDealId)}`);
                                showAlert("Deal link copied to clipboard.", { variant: "info", title: "Link copied" });
                              }}
                              className="px-2 py-1 text-xs text-sky-400 hover:bg-sky-500/10 rounded-md transition-colors"
                            >
                              Copy Link
                            </button>
                          )}
                          {rowCollection && rowDealId && (
                            <button
                              onClick={() => navigate(`/admin/deals/gallery-review?collection=${encodeURIComponent(rowCollection)}&deal_id=${encodeURIComponent(rowDealId)}`)}
                              className="px-2 py-1 text-xs text-violet-400 hover:bg-violet-500/10 rounded-md transition-colors"
                            >
                              Review Gallery
                            </button>
                          )}
                          {rowCollection && rowDealId && (
                            <button
                              onClick={() => handleGenerateAiFlyer(rowCollection, rowDealId)}
                              disabled={flyerGenBusy.has(rowDealId)}
                              title={flyerGenError[rowDealId] || "Generate a new AI flyer from this deal's data using its category template"}
                              className="px-2 py-1 text-xs text-fuchsia-400 hover:bg-fuchsia-500/10 rounded-md transition-colors disabled:opacity-50"
                            >
                              {flyerGenBusy.has(rowDealId) ? "Generating…" : flyerGenError[rowDealId] ? "Retry AI Flyer" : "Generate AI Flyer"}
                            </button>
                          )}
                          {rowNeedsDateReview && rowCollection && rowDealId && (
                            <>
                              <button
                                onClick={handleDateExpiryApprove}
                                disabled={rowBusy}
                                title="Approve as-is and publish. To correct the dates first, use the deal's own edit form, then Approve."
                                className="px-2 py-1 text-xs text-emerald-400 hover:bg-emerald-500/10 rounded-md transition-colors disabled:opacity-50"
                              >
                                {rowBusy ? "Working…" : "Approve Dates"}
                              </button>
                              <button
                                onClick={handleDateExpiryReject}
                                disabled={rowBusy}
                                className="px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {!isActive && !rowNeedsDateReview && rowCollection && rowDealId && (
                            <button
                              onClick={() => handleRowToggle(true)}
                              disabled={rowBusy}
                              className="px-2 py-1 text-xs text-emerald-400 hover:bg-emerald-500/10 rounded-md transition-colors disabled:opacity-50"
                            >
                              {rowBusy ? "Publishing…" : "Publish"}
                            </button>
                          )}
                          {isActive && rowCollection && rowDealId && (
                            <button
                              onClick={() => handleRowToggle(false)}
                              disabled={rowBusy}
                              className="px-2 py-1 text-xs text-amber-400 hover:bg-amber-500/10 rounded-md transition-colors disabled:opacity-50"
                            >
                              {rowBusy ? "Unpublishing…" : "Unpublish"}
                            </button>
                          )}
                          <button onClick={() => setDeleteTarget(deal)} className="px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors">Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data && data.total > 0 && (
        <Pagination
          page={page - 1}
          pageSize={pageSize}
          total={data.total}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={setPageSize}
        />
      )}

      {/* Stuck / Shadow read-only detail drawer */}
      {selectedDeal && viewMode === "stuck_shadow" && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={() => { setSelectedDeal(null); setStuckShadowDetail(null); }}>
          <div className="w-full max-w-lg h-full bg-gray-950 border-l border-gray-800 overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">Stuck / Shadow Deal</h2>
              <button onClick={() => { setSelectedDeal(null); setStuckShadowDetail(null); }} className="text-gray-500 hover:text-gray-300 text-xl leading-none">×</button>
            </div>

            {stuckShadowLoading && <p className="text-sm text-gray-500"><Spinner /></p>}

            {!stuckShadowLoading && stuckShadowDetail && (
              <div className="space-y-5">
                <MetaField label="Title" value={stuckShadowDetail.deal?.title} />
                <MetaField label="Deal ID" value={stuckShadowDetail.deal?.id || stuckShadowDetail.deal?._id} mono />
                <MetaField label="Status" value={stuckShadowDetail.deal_record_status || undefined} />
                <MetaField label="Attempted target collection" value={stuckShadowDetail.needs_manual_recategorisation_target || undefined} />
                <MetaField label="Attempted primary slug" value={stuckShadowDetail.needs_manual_recategorisation_primary_slug || undefined} />
                <MetaField label="Created" value={stuckShadowDetail.deal?.created_at ? formatIST(stuckShadowDetail.deal.created_at) : undefined} />

                <div>
                  <p className="text-[11px] text-gray-500 mb-1">Full failure reason</p>
                  <pre className="text-xs text-gray-300 whitespace-pre-wrap break-words bg-gray-900 border border-gray-800 rounded-lg p-3">
                    {stuckShadowDetail.reason || "No reason recorded."}
                  </pre>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Linked AI Analysis Job</p>
                  {!stuckShadowDetail.analysis_job && (
                    <p className="text-xs text-gray-600">No analysis job found for this deal.</p>
                  )}
                  {stuckShadowDetail.analysis_job && (
                    <div className="space-y-2 bg-gray-900 border border-gray-800 rounded-lg p-3">
                      <MetaField label="Status" value={stuckShadowDetail.analysis_job.status} />
                      <MetaField label="Retry count" value={String(stuckShadowDetail.analysis_job.retry_count ?? 0)} />
                      <MetaField label="Updated" value={stuckShadowDetail.analysis_job.updated_at ? formatIST(stuckShadowDetail.analysis_job.updated_at) : undefined} />
                      <div>
                        <p className="text-[11px] text-gray-500 mb-1">Full job error</p>
                        <pre className="text-xs text-gray-300 whitespace-pre-wrap break-words">
                          {stuckShadowDetail.analysis_job.error || "No error recorded."}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedDeal && viewMode === "typed" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => { setSelectedDeal(null); setRawOpen(false); }}>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-full max-w-2xl shadow-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const d = selectedDeal;
              const countries: string[] = d.destination?.countries ?? [];
              const cities: string[] = d.destination?.cities ?? [];
              const states: string[] = d.destination?.states ?? [];
              const tags: string[] = d.tags ?? [];
              const activities: string[] = d.activities ?? [];
              // `categories` arrives in two shapes: the admin list's nested
              // {primary:{slug,...}} form and the typed-deal document's flat
              // {primary_slug, subcategory_slugs, secondary_slugs, ...} form
              // (what the ?deal_id= deep-link fetch returns). Read both.
              const categorySlug = d.categories?.primary?.slug || d.categories?.primary_slug || d.category_slug || d.category;
              const subCategorySlugs: string[] = d.categories?.primary?.sub_slugs ?? d.categories?.subcategory_slugs ?? [];
              const isActive = d.lifecycle?.is_active === true;
              // Same two-shape story as `categories`: the admin list flattens the
              // seller onto dealer_name/dealer_id, the typed document nests it
              // under `dealer` — the Seller section read only the flat pair and
              // therefore showed "—" for deep-linked deals.
              const sellerId: string | undefined = d.dealer_id || d.seller_id || d.dealer?.id;
              const sellerName: string | undefined = d.dealer_name || d.seller_name || d.dealer?.name;
              const dealCollection = d.collection_name || d.collection;
              const dealId = d.id || d._id;
              const handleUnpublish = async () => {
                if (!dealCollection || !dealId || unpublishBusy) return;
                setUnpublishBusy(true);
                try {
                  const updated = await adminService.unpublishDeal(dealCollection, dealId);
                  setSelectedDeal((prev: any) => (prev ? { ...prev, lifecycle: { ...prev.lifecycle, is_active: false }, ...updated } : prev));
                } catch {
                  // best-effort — the badge below just won't flip if this fails
                } finally {
                  setUnpublishBusy(false);
                }
              };
              const applyPublishError = (err: any) => {
                const detail = err?.response?.data?.detail;
                if (typeof detail === "string") {
                  setPublishError(detail);
                  setPublishErrorDetail(null);
                } else if (detail && typeof detail === "object" && typeof detail.message === "string") {
                  setPublishError(null);
                  setPublishErrorDetail(detail);
                } else if (detail) {
                  setPublishError("Failed to publish deal.");
                  setPublishErrorDetail(null);
                } else {
                  setPublishError("Failed to publish deal.");
                  setPublishErrorDetail(null);
                }
              };
              const destinationLabel = () => {
                const countries: string[] = d.destination?.countries || (d.destination?.country ? [d.destination.country] : []);
                return countries.join(", ") || "the deal's destination";
              };
              const handlePublish = async () => {
                if (!dealCollection || !dealId || publishBusy) return;
                setPublishBusy(true);
                setPublishError(null);
                setPublishErrorDetail(null);
                setPublishNotice(null);
                try {
                  const updated = await adminService.publishDeal(dealCollection, dealId);
                  setSelectedDeal((prev: any) => (prev ? { ...prev, lifecycle: { ...prev.lifecycle, is_active: true }, ...updated } : prev));
                } catch (err: any) {
                  // Seller never declared this market — let the admin choose to
                  // auto-add it (whole country) or go add it manually, instead of
                  // silently granting it on their behalf.
                  applyPublishError(err);
                } finally {
                  setPublishBusy(false);
                }
              };
              const handleAutoAddMarketAndPublish = async () => {
                if (!dealCollection || !dealId || publishBusy) return;
                setPublishBusy(true);
                setPublishError(null);
                try {
                  const updated = await adminService.publishDeal(dealCollection, dealId, { addMissingMarket: true });
                  setSelectedDeal((prev: any) => (prev ? { ...prev, lifecycle: { ...prev.lifecycle, is_active: true }, ...updated } : prev));
                  setPublishErrorDetail(null);
                  setPublishNotice(`Published. ${destinationLabel()} wasn't in this seller's approved markets yet, so it was added automatically.`);
                } catch (err2: any) {
                  const detail2 = err2?.response?.data?.detail;
                  const detail2Message = typeof detail2 === "string" ? detail2 : detail2?.message;
                  setPublishError(
                    `Tried to auto-add ${destinationLabel()} to this seller's markets, but publishing still failed${detail2Message ? `: ${detail2Message}` : "."}`,
                  );
                  setPublishErrorDetail(null);
                } finally {
                  setPublishBusy(false);
                }
              };
              const handleSaveBoosts = async () => {
                if (!dealId || boostBusy) return;
                setBoostBusy(true);
                setBoostError(null);
                setBoostSaved(false);
                try {
                  const updated = await adminService.updateDeal(dealId, {
                    admin_likes_boost: Number(boostForm.admin_likes_boost) || 0,
                    admin_saves_boost: Number(boostForm.admin_saves_boost) || 0,
                  });
                  setSelectedDeal((prev: any) => (prev ? { ...prev, ...updated } : prev));
                  setBoostSaved(true);
                } catch (err: any) {
                  const detail = err?.response?.data?.detail;
                  setBoostError(typeof detail === "string" ? detail : "Failed to save boosts.");
                } finally {
                  setBoostBusy(false);
                }
              };
              const handleOpenMarketMap = () => {
                if (!sellerId) return;
                const params = new URLSearchParams();
                params.set("market_user", sellerId);
                if (sellerName) params.set("market_user_name", sellerName);
                navigate(`/admin/users?${params.toString()}`);
              };
              const currency = d.pricing?.currency || "INR";
              const orig = d.pricing?.original_price;
              const disc = d.pricing?.discounted_price;
              // media.gallery entries are now { key, status, ... } objects rather
              // than bare strings; admin sees every item regardless of moderation
              // status, so only the key needs extracting here (no filtering).
              const galleryRawSource = d.media?.gallery ?? d.gallery ?? [];
              // `_broken: true` (set by the admin media resolver) means the S3
              // object behind this entry is gone — the entry is now kept and
              // flagged rather than silently dropped, so surface it.
              const galleryRaw: { key: string; broken: boolean }[] = (galleryRawSource as unknown[])
                .map((g) =>
                  typeof g === "string"
                    ? { key: g, broken: false }
                    : { key: (g as { key?: string })?.key ?? "", broken: Boolean((g as { _broken?: boolean })?._broken) },
                )
                .filter((g) => Boolean(g.key));
              // Scalar media fields the backend could not resolve. Labels are
              // field paths, e.g. "image_clean" / "media.video_url".
              const brokenFields = new Set<string>((d._broken_fields as string[] | undefined) ?? []);
              const fieldBroken = (...labels: string[]) => labels.some((l) => brokenFields.has(l));
              const originalImage = d.media?.image || d.image;
              const cleanedImage = d.media?.image_clean || d.image_clean;
              const originalPdf = d.media?.pdf_url || d.pdf_url;
              const originalDocument = d.media?.document_url;
              const originalVideo = d.media?.video_url || d.video_url;
              const generatedFlyer = d.ai_generated_flyer;
              // Labelled media: what the seller originally uploaded (image/pdf/video),
              // the PII-cleaned version of it, the AI-generated flyer (from doc/pdf
              // text or title+description when no asset was uploaded), and any
              // remaining gallery images not already covered by the above.
              const namedUrls = new Set(
                [originalImage, cleanedImage, generatedFlyer]
                  .filter((u): u is string => Boolean(u))
                  .map((u) => resolveMediaUrl(u)),
              );
              const labeledMedia: { label: string; url: string; isVideo?: boolean; isDocument?: boolean; broken?: boolean }[] = [];
              if (originalImage) labeledMedia.push({ label: "Original", url: resolveMediaUrl(originalImage)!, broken: fieldBroken("image", "media.image") });
              if (originalVideo) labeledMedia.push({ label: "Original (Video)", url: resolveMediaUrl(originalVideo)!, isVideo: true, broken: fieldBroken("video_url", "media.video_url") });
              // pdf_url now mirrors the original file for docx uploads too (not just
              // native PDFs), so document_url only ever duplicates it — show one tile.
              if (originalPdf) {
                labeledMedia.push({ label: "Original (Document)", url: resolveMediaUrl(originalPdf)!, isDocument: true, broken: fieldBroken("pdf_url", "media.pdf_url") });
              } else if (originalDocument) {
                labeledMedia.push({ label: "Original (Document)", url: resolveMediaUrl(originalDocument)!, isDocument: true });
              }
              // image_clean and ai_generated_flyer are the same AI-synthesized asset
              // (flyer generation now writes both fields together) — show one tile,
              // preferring the direct flyer URL over the image_clean S3 key.
              const aiGeneratedUrl = generatedFlyer || cleanedImage;
              if (aiGeneratedUrl) {
                labeledMedia.push({
                  label: "AI Generated",
                  url: resolveMediaUrl(aiGeneratedUrl)!,
                  broken: generatedFlyer
                    ? fieldBroken("ai_generated_flyer")
                    : fieldBroken("image_clean", "media.image_clean"),
                });
              }
              for (const g of galleryRaw) {
                if (isVideoMedia(g.key)) continue;
                const resolved = resolveMediaUrl(g.key);
                if (!resolved || namedUrls.has(resolved)) continue;
                namedUrls.add(resolved);
                labeledMedia.push({ label: "Gallery", url: resolved, broken: g.broken });
              }
              return (
                <>
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
                    <div className="min-w-0 pr-4">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        {d.item_type && (
                          <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${typeColor(d.item_type)}`}>{d.item_type}</span>
                        )}
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${isActive ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-green-400" : "bg-red-400"}`} />
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <h2 className="text-lg font-semibold text-white break-words">{d.title || "Untitled"}</h2>
                      {d.subtitle && <p className="text-sm text-gray-400 mt-0.5 break-words">{d.subtitle}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!isActive && dealCollection && dealId && (
                        <button
                          onClick={handlePublish}
                          disabled={publishBusy}
                          className="px-2.5 py-1 rounded-md text-xs font-medium bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                        >
                          {publishBusy ? "Publishing…" : "Publish"}
                        </button>
                      )}
                      {isActive && dealCollection && dealId && (
                        <button
                          onClick={handleUnpublish}
                          disabled={unpublishBusy}
                          className="px-2.5 py-1 rounded-md text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                        >
                          {unpublishBusy ? "Unpublishing…" : "Unpublish"}
                        </button>
                      )}
                      <button onClick={() => { setSelectedDeal(null); setRawOpen(false); }} className="text-gray-500 hover:text-white transition-colors">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  </div>
                  {/* Tabs */}
                  <div className="flex items-center gap-1 border-b border-gray-800 mb-4">
                    {(["details", "dates", "pinecone"] as const).map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setDetailTab(tab)}
                        className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                          detailTab === tab
                            ? "border-indigo-500 text-white"
                            : "border-transparent text-gray-500 hover:text-gray-300"
                        }`}
                      >
                        {tab === "details" ? "Details" : tab === "dates" ? "Dates" : "Pinecone"}
                      </button>
                    ))}
                  </div>

                  {detailTab === "details" && (
                    <>
                  {publishNotice && (
                    <p className="text-xs text-emerald-400 mb-3 whitespace-pre-line">{publishNotice}</p>
                  )}
                  {publishError && (
                    <div className="mb-3">
                      <p className="text-xs text-red-400 whitespace-pre-line">{publishError}</p>
                      {d.dealer_id && (
                        <button
                          onClick={handleOpenMarketMap}
                          className="mt-2 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors"
                        >
                          Open Seller's Market Map
                        </button>
                      )}
                    </div>
                  )}
                  {publishErrorDetail && (
                    <div className="mb-3">
                      <p className="text-xs text-red-400 whitespace-pre-line">{publishErrorDetail.message}</p>
                      <div className="flex items-center gap-2 mt-2">
                        {publishErrorDetail.code === "market_not_covered" && (
                          <button
                            onClick={handleAutoAddMarketAndPublish}
                            disabled={publishBusy}
                            className="px-2.5 py-1 rounded-md text-xs font-medium bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                          >
                            {publishBusy ? "Adding…" : `Auto-add ${destinationLabel()} & Publish`}
                          </button>
                        )}
                        {(publishErrorDetail.code === "market_not_covered" || publishErrorDetail.code === "market_pending_review") && d.dealer_id && (
                          <button
                            onClick={handleOpenMarketMap}
                            className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors"
                          >
                            Add Manually (Open Seller's Market Map)
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="space-y-5">
                    {/* Pricing */}
                    {(orig != null || disc != null) && (() => {
                      const hasDiscount = orig != null && disc != null && orig > disc;
                      const discountPct = hasDiscount ? Math.round(((orig - disc) / orig) * 100) : null;
                      return (
                        <Section title="Pricing">
                          <div className="flex flex-col gap-2">
                            <div className="flex flex-wrap gap-x-6 gap-y-2">
                              {orig != null && (
                                <div>
                                  <p className="text-[10px] uppercase tracking-wide text-gray-500">Original price</p>
                                  <p className={hasDiscount ? "text-sm text-gray-500 line-through" : "text-xl font-semibold text-white"}>
                                    {currency} {Number(orig).toLocaleString()}
                                  </p>
                                </div>
                              )}
                              {disc != null && (
                                <div>
                                  <p className="text-[10px] uppercase tracking-wide text-gray-500">Discounted price</p>
                                  <p className="text-xl font-semibold text-white">{currency} {Number(disc).toLocaleString()}</p>
                                </div>
                              )}
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-gray-500">Discount</p>
                                <p className={discountPct ? "text-sm font-medium text-emerald-400" : "text-sm text-gray-500"}>
                                  {discountPct != null && discountPct > 0 ? `${discountPct}% off` : "None"}
                                </p>
                              </div>
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-gray-500">Currency</p>
                                <p className="text-sm text-gray-300">{currency}</p>
                              </div>
                            </div>
                          </div>
                        </Section>
                      );
                    })()}

                    {/* Product info — identity/type fields that used to only exist in Raw data */}
                    <StructuredSection
                      title="Product Info"
                      value={{
                        item_type: d.item_type,
                        service_type: d.service_type,
                        collection: dealCollection,
                        category_slug: categorySlug,
                        is_flash: d.is_flash || undefined,
                        flash_ends_at: d.flash_ends_at,
                        is_in_packages: d.is_in_packages,
                        rail_tags: d.rail_tags,
                        schema_version: d.schema_version,
                      }}
                    />

                    {/* Lifecycle status + admin moderation gate */}
                    <StructuredSection
                      title="Status & Review"
                      value={{ lifecycle: d.lifecycle, admin_review: d.admin_review }}
                    />

                    {/* Type-specific block — package / stay / flight / cruise / experience /
                        transfer / media_asset / visa / insurance / custom. Rendered
                        generically so every schema field (highlights, inclusions,
                        exclusions, itinerary, amenities, cabins, route, ...) shows up. */}
                    {TYPED_DEAL_BLOCK_KEYS.map((key) => (
                      <StructuredSection key={key} title={humanizeKey(key)} value={d[key]} />
                    ))}

                    {/* Everything on `pricing` beyond the headline figures above —
                        pricing_tiers, by_room_type / by_cabin_type / by_pax_type,
                        base_fare, charge_basis, validity_periods, ... */}
                    {(() => {
                      if (!isPlainObject(d.pricing)) return null;
                      const rest = Object.fromEntries(
                        Object.entries(d.pricing).filter(
                          ([k]) => !["currency", "original_price", "discounted_price"].includes(k),
                        ),
                      );
                      return <StructuredSection title="Pricing Details" value={rest} />;
                    })()}

                    {/* Terms / capacity / inventory */}
                    <StructuredSection title="Terms" value={d.terms} />
                    <StructuredSection title="Inventory" value={d.inventory} />

                    {/* Seller-entered free-form extra fields ({name, value} pairs) */}
                    {(() => {
                      const extras: any[] = Array.isArray(d.extras) ? d.extras : [];
                      if (extras.length === 0) return null;
                      const obj: Record<string, unknown> = {};
                      extras.forEach((e, i) => {
                        if (!isPlainObject(e)) return;
                        obj[String(e.name ?? `field_${i + 1}`)] = e.value;
                      });
                      return <StructuredSection title="Extra Fields" value={obj} />;
                    })()}

                    {/* Engagement boosts */}
                    <Section title="Engagement boosts (admin)">
                      <div className="flex flex-col gap-3">
                        <p className="text-xs text-gray-500">
                          Added on top of real likes/saves shown to users — does not affect real user like/save data.
                        </p>
                        <div className="flex flex-wrap gap-4">
                          <div>
                            <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Like boost (admin)</label>
                            <p className="text-xs text-gray-500 mb-1">
                              Real likes: <span className="text-gray-300">{selectedDeal.likes ?? 0}</span>
                              {" → "}Total: <span className="text-white font-medium">{(selectedDeal.likes ?? 0) + (Number(boostForm.admin_likes_boost) || 0)}</span>
                            </p>
                            <input
                              type="number"
                              value={boostForm.admin_likes_boost}
                              onChange={(e) => setBoostForm({ ...boostForm, admin_likes_boost: e.target.value })}
                              className="w-28 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Save boost (admin)</label>
                            <p className="text-xs text-gray-500 mb-1">
                              Real saves: <span className="text-gray-300">{selectedDeal.save_count ?? 0}</span>
                              {" → "}Total: <span className="text-white font-medium">{(selectedDeal.save_count ?? 0) + (Number(boostForm.admin_saves_boost) || 0)}</span>
                            </p>
                            <input
                              type="number"
                              value={boostForm.admin_saves_boost}
                              onChange={(e) => setBoostForm({ ...boostForm, admin_saves_boost: e.target.value })}
                              className="w-28 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                            />
                          </div>
                          <button
                            onClick={handleSaveBoosts}
                            disabled={boostBusy}
                            className="self-end px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 transition-colors disabled:opacity-50"
                          >
                            {boostBusy ? "Saving…" : "Save"}
                          </button>
                        </div>
                        {boostError && <p className="text-xs text-red-400">{boostError}</p>}
                        {boostSaved && !boostError && <p className="text-xs text-emerald-400">Saved.</p>}
                      </div>
                    </Section>

                    {/* Destination */}
                    {(countries.length > 0 || states.length > 0 || cities.length > 0) && (
                      <Section title="Destination">
                        <div className="flex flex-col gap-2">
                          {countries.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-[10px] uppercase tracking-wide text-gray-500 w-14 shrink-0">Country</span>
                              <ChipList items={countries} className="bg-sky-500/10 text-sky-300" />
                            </div>
                          )}
                          {states.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-[10px] uppercase tracking-wide text-gray-500 w-14 shrink-0">State</span>
                              <ChipList items={states} className="bg-fuchsia-500/10 text-fuchsia-300" />
                            </div>
                          )}
                          {cities.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-[10px] uppercase tracking-wide text-gray-500 w-14 shrink-0">City</span>
                              <ChipList items={cities} className="bg-violet-500/10 text-violet-300" />
                            </div>
                          )}
                        </div>
                      </Section>
                    )}

                    {/* Category */}
                    {categorySlug && (() => {
                      const primary = d.categories?.primary ?? (d.categories?.primary_slug
                        ? { slug: d.categories.primary_slug, group_slug: d.categories.group_slug }
                        : undefined);
                      const secondaryNames: string[] = d.categories?.secondary_category_names ?? [];
                      const secondary: { slug: string; group_slug?: string | null; name?: string | null }[] =
                        d.categories?.secondary
                        ?? (d.categories?.secondary_slugs ?? []).map((slug: string, i: number) => ({
                          slug,
                          name: secondaryNames[i] ?? null,
                          group_slug: (d.categories?.secondary_group_slugs ?? [])[i] ?? null,
                        }));
                      const subDisplayNames: string[] = primary?.sub_display_names ?? [];
                      const pkg = d.categories?.package ?? (
                        d.categories?.package_primary_slug || (d.categories?.package_secondary_slugs ?? []).length > 0
                          ? { primary_slug: d.categories?.package_primary_slug, secondary_slugs: d.categories?.package_secondary_slugs ?? [] }
                          : undefined
                      );
                      return (
                        <Section title="Category">
                          <div className="flex flex-col gap-3">
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Primary category</p>
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-amber-400 text-xs font-mono bg-amber-500/10 px-2 py-1 rounded-md">{categorySlug}</span>
                                {primary?.group_slug && (
                                  <span className="text-gray-400 text-xs px-2 py-1 rounded-md bg-gray-800">
                                    group: <span className="font-mono">{primary.group_display_name || primary.group_slug}</span>
                                    {primary.group_display_name && <span className="text-gray-600"> ({primary.group_slug})</span>}
                                  </span>
                                )}
                              </div>
                            </div>

                            {subCategorySlugs.length > 0 && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Subcategories</p>
                                <div className="flex flex-wrap items-center gap-1.5">
                                  {subCategorySlugs.map((slug, i) => (
                                    <span key={slug} className="text-sky-300/80 text-xs font-mono bg-sky-500/5 px-2 py-1 rounded-md">
                                      {subDisplayNames[i] || slug}
                                      {subDisplayNames[i] && subDisplayNames[i] !== slug && (
                                        <span className="text-sky-300/40"> ({slug})</span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {secondary.length > 0 && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Secondary categories</p>
                                <div className="flex flex-wrap items-center gap-1.5">
                                  {secondary.map((s) => (
                                    <span key={s.slug} className="text-amber-300/70 text-xs font-mono bg-amber-500/5 px-2 py-1 rounded-md">
                                      {s.name || s.slug}
                                      {s.name && s.name !== s.slug && <span className="text-amber-300/40"> ({s.slug})</span>}
                                      {s.group_slug && <span className="text-gray-600"> · {s.group_slug}</span>}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {pkg && (pkg.primary_slug || (pkg.secondary_slugs ?? []).length > 0) && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Package category</p>
                                <div className="flex flex-wrap items-center gap-1.5">
                                  {pkg.primary_slug && (
                                    <span className="text-violet-300 text-xs font-mono bg-violet-500/10 px-2 py-1 rounded-md">{pkg.primary_slug}</span>
                                  )}
                                  {(pkg.secondary_slugs ?? []).map((slug: string) => (
                                    <span key={slug} className="text-violet-300/70 text-xs font-mono bg-violet-500/5 px-2 py-1 rounded-md">{slug}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </Section>
                      );
                    })()}

                    {/* Tags / Activities */}
                    {tags.length > 0 && (
                      <Section title="Tags">
                        <ChipList items={tags} className="bg-emerald-500/10 text-emerald-300" />
                      </Section>
                    )}
                    {activities.length > 0 && (
                      <Section title="Activities">
                        <ChipList items={activities} className="bg-pink-500/10 text-pink-300" />
                      </Section>
                    )}

                    {/* User title + description — original seller input, never AI-overwritten */}
                    {(d.user_title || d.title) && (
                      <Section title="User Title">
                        <p className="text-sm text-gray-200 font-medium">{d.user_title || d.title}</p>
                        {d.subtitle && <p className="text-xs text-gray-500 mt-0.5">{d.subtitle}</p>}
                      </Section>
                    )}
                    {d.user_description && (
                      <Section title="User Description">
                        <p className="text-sm text-gray-300 whitespace-pre-wrap break-words">{d.user_description}</p>
                      </Section>
                    )}

                    {/* AI-generated title + description */}
                    {(d.ai_suggested_title || d.ai_suggested_description) && (
                      <Section title="AI-Generated Content">
                        {d.ai_suggested_title && (
                          <p className="text-sm text-indigo-300 font-medium mb-1">{d.ai_suggested_title}</p>
                        )}
                        {d.ai_suggested_description && (
                          <p className="text-xs text-gray-400 whitespace-pre-wrap break-words">{d.ai_suggested_description}</p>
                        )}
                      </Section>
                    )}

                    {/* AI-enriched summary (upserted as enriched_summary / ai_description) */}
                    {(d.enriched_summary || d.ai_description) && (
                      <Section title="AI-Enriched Summary">
                        <p className="text-sm text-gray-300 whitespace-pre-wrap break-words">{d.enriched_summary || d.ai_description}</p>
                      </Section>
                    )}

                    {/* Original description */}
                    {(d.description || d.summary) && (
                      <Section title="Description">
                        <p className="text-sm text-gray-300 whitespace-pre-wrap break-words">{d.description || d.summary}</p>
                      </Section>
                    )}

                    {/* Seller */}
                    <Section title="Seller">
                      <div className="flex items-center gap-3">
                        <p className="text-sm text-gray-200">{sellerName || "—"}</p>
                        {sellerId && (() => {
                          const rowKey = `modal:${d.id}:${sellerId}`;
                          return (
                            <span
                              className="relative inline-block"
                              onMouseEnter={(e) => showSellerPopover(rowKey, sellerId, sellerName || "Seller", e.currentTarget.getBoundingClientRect())}
                              onMouseLeave={hideSellerPopover}
                            >
                              <button className="text-xs text-indigo-400 hover:text-indigo-300 underline-offset-2 hover:underline">View seller details</button>
                              {hoveredSeller?.rowKey === rowKey && (
                                <SellerPopover
                                  state={sellerCache[sellerId]}
                                  fallbackName={sellerName}
                                  anchorRect={hoveredSeller.anchorRect}
                                  onMouseEnter={() => { if (hoverHideTimer.current) { clearTimeout(hoverHideTimer.current); hoverHideTimer.current = null; } }}
                                  onMouseLeave={hideSellerPopover}
                                />
                              )}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2">
                        <MetaField label="Seller ID" value={sellerId} mono />
                        <MetaField
                          label="Seller location"
                          value={[d.dealer?.city, d.dealer?.state, d.dealer?.country].filter(Boolean).join(", ") || undefined}
                        />
                      </div>
                    </Section>

                    {/* On-demand AI flyer generation for this one deal */}
                    {dealCollection && dealId && (
                      <Section title="AI Flyer">
                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={() => handleGenerateAiFlyer(dealCollection, dealId)}
                            disabled={flyerGenBusy.has(dealId)}
                            className="px-2.5 py-1 rounded-md text-xs font-medium bg-fuchsia-500/10 text-fuchsia-400 hover:bg-fuchsia-500/20 transition-colors disabled:opacity-50"
                          >
                            {flyerGenBusy.has(dealId)
                              ? "Generating…"
                              : flyerGenError[dealId]
                                ? "Retry AI Flyer"
                                : "Generate AI Flyer"}
                          </button>
                          <span className="text-[11px] text-gray-500">
                            Regenerates the AI flyer from this deal's data using its category template.
                          </span>
                        </div>
                        {flyerGenError[dealId] && (
                          <p className="text-xs text-red-400 mt-2 whitespace-pre-line">{flyerGenError[dealId]}</p>
                        )}
                      </Section>
                    )}

                    {/* Media */}
                    {labeledMedia.length > 0 && (
                      <Section title={`Media (${labeledMedia.length})`}>
                        <div className="flex flex-wrap gap-3">
                          {labeledMedia.map((m, i) => {
                            // Server-confirmed dead S3 object (`_broken` / `_broken_fields`)
                            // OR a load failure detected in the browser.
                            const isBroken = Boolean(m.broken) || brokenMediaUrls.has(m.url);
                            const markBroken = () => setBrokenMediaUrls((prev) => (prev.has(m.url) ? prev : new Set(prev).add(m.url)));
                            return (
                              <div key={i} className="flex flex-col items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => (isBroken ? navigate(`/admin/deals/gallery-review?collection=${encodeURIComponent(dealCollection || "")}&deal_id=${encodeURIComponent(dealId || "")}`) : m.isDocument ? window.open(m.url, "_blank", "noopener,noreferrer") : setPreviewMedia(m))}
                                  className="relative"
                                >
                                  {isBroken ? (
                                    <span className="w-24 h-24 flex flex-col items-center justify-center gap-1 rounded-lg border border-red-500/50 bg-red-500/10 text-red-400">
                                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
                                      <span className="text-[9px] leading-tight">Missing — click to fix</span>
                                    </span>
                                  ) : m.isVideo ? (
                                    <video src={m.url} onError={markBroken} className="w-24 h-24 object-cover rounded-lg border border-gray-800 hover:border-indigo-500 transition-colors bg-black" />
                                  ) : m.isDocument ? (
                                    <span className="w-24 h-24 flex items-center justify-center rounded-lg border border-gray-800 hover:border-indigo-500 transition-colors bg-gray-900">
                                      <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                    </span>
                                  ) : (
                                    <img src={m.url} alt="" onError={markBroken} className="w-24 h-24 object-cover rounded-lg border border-gray-800 hover:border-indigo-500 transition-colors" />
                                  )}
                                </button>
                                <span className={`text-[10px] text-center max-w-[96px] truncate ${isBroken ? "text-red-400" : "text-gray-500"}`}>{m.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </Section>
                    )}

                    {/* Lifecycle / meta */}
                    <Section title="Lifecycle">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        <MetaField label="Created" value={d.created_at ? formatIST(d.created_at) : undefined} />
                        <MetaField label="Updated" value={d.updated_at ? formatIST(d.updated_at) : undefined} />
                        <MetaField label="Active" value={isActive ? "Yes" : "No"} />
                        <MetaField label="Deal ID" value={d.id || d._id} mono />
                        <MetaField label="Created by" value={d.created_by} mono />
                        <MetaField label="Updated by" value={d.updated_by} mono />
                        <MetaField label="Collection" value={dealCollection} mono />
                        <MetaField label="Tenant" value={d.tenant_id} mono />
                      </div>
                    </Section>

                    {/* Anything on the document without a dedicated section above —
                        keeps the modal complete as the typed schema grows. */}
                    {(() => {
                      const rest = Object.fromEntries(
                        Object.entries(d).filter(
                          ([k, v]) => !RENDERED_DEAL_KEYS.has(k) && !k.startsWith("_") && !isEmptyValue(v),
                        ),
                      );
                      return <StructuredSection title="Other Fields" value={rest} />;
                    })()}

                    {/* AI-extracted source payload (what the analyzer read off the
                        flyer) — collapsed, it can be very large. */}
                    {!isEmptyValue(d.ai_extracted_raw ?? d.ai_extracted) && (
                      <details>
                        <summary className="text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-300">
                          AI Extracted (source)
                        </summary>
                        <div className="mt-2">
                          <StructuredValue value={d.ai_extracted_raw ?? d.ai_extracted} />
                        </div>
                      </details>
                    )}

                    {/* Raw data (collapsed) */}
                    <div>
                      <button
                        onClick={() => setRawOpen((v) => !v)}
                        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                      >
                        <svg className={`w-3.5 h-3.5 transition-transform ${rawOpen ? "rotate-90" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        Raw data
                      </button>
                      {rawOpen && (
                        <pre className="mt-2 text-xs text-gray-300 bg-gray-800 rounded-xl p-4 overflow-auto whitespace-pre-wrap max-h-72">
                          {JSON.stringify(d, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                    </>
                  )}

                  {detailTab === "dates" && (() => {
                    const validity = d.validity ?? {};
                    const specificDates: string[] = validity.dates ?? [];
                    const validityPeriods: any[] = validity.validity_periods ?? d.pricing?.validity_periods ?? [];
                    const blackoutDates: string[] = Array.from(
                      new Set([...(d.terms?.blackout_dates ?? []), ...(d.package?.blackout_dates ?? [])]),
                    );
                    const sailings: any[] = d.cruise?.sailings ?? [];
                    const portsOfCall: any[] = d.cruise?.ports_of_call ?? [];
                    const startsAt: string[] = d.experience?.starts_at ?? [];
                    const operatesOn: string[] = d.experience?.operates_on ?? [];
                    const stayPackages: any[] = (d.stay?.packages ?? []).filter(
                      (p: any) => p?.validity_start || p?.validity_end,
                    );
                    const mediaScheduledAt: string | undefined = d.media_asset?.scheduled_at;
                    const visa = d.visa;
                    const insurance = d.insurance;
                    const custom = d.custom;
                    const hasServiceDuration = !!(visa || insurance || custom);
                    const fmt = (v: unknown) => (v ? formatIST(String(v)) : "—");
                    const hasAnyDates =
                      specificDates.length > 0 ||
                      validityPeriods.length > 0 ||
                      blackoutDates.length > 0 ||
                      sailings.length > 0 ||
                      portsOfCall.length > 0 ||
                      startsAt.length > 0 ||
                      operatesOn.length > 0 ||
                      stayPackages.length > 0 ||
                      !!mediaScheduledAt ||
                      hasServiceDuration ||
                      validity.valid_from ||
                      validity.valid_until ||
                      validity.booking_deadline ||
                      validity.travel_period ||
                      d.flash_ends_at;
                    return (
                      <div className="space-y-5">
                        {!hasAnyDates && (
                          <p className="text-sm text-gray-500">No date/validity data on this deal.</p>
                        )}

                        {(validity.valid_from || validity.valid_until) && (
                          <Section title="Validity Range">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                              <MetaField label="Valid from" value={fmt(validity.valid_from)} />
                              <MetaField label="Valid until" value={fmt(validity.valid_until)} />
                            </div>
                          </Section>
                        )}

                        {validity.travel_period && (validity.travel_period.start || validity.travel_period.end) && (
                          <Section title="Travel Period">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                              <MetaField label="Start" value={fmt(validity.travel_period.start)} />
                              <MetaField label="End" value={fmt(validity.travel_period.end)} />
                            </div>
                          </Section>
                        )}

                        {validity.booking_deadline && (
                          <Section title="Booking Deadline">
                            <p className="text-sm text-gray-200">{fmt(validity.booking_deadline)}</p>
                          </Section>
                        )}

                        {d.flash_ends_at && (
                          <Section title="Flash Deal Ends">
                            <p className="text-sm text-gray-200">{fmt(d.flash_ends_at)}</p>
                          </Section>
                        )}

                        {specificDates.length > 0 && (
                          <Section title={`Specific Travel Dates (${specificDates.length})`}>
                            <div className="flex flex-wrap gap-1.5">
                              {specificDates.map((dt, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 rounded bg-sky-500/10 text-sky-300">
                                  {fmt(dt)}
                                </span>
                              ))}
                            </div>
                          </Section>
                        )}

                        {validityPeriods.length > 0 && (
                          <Section title={`Validity Periods / Batches (${validityPeriods.length})`}>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500 uppercase tracking-wide text-[10px]">
                                    <th className="text-left pb-1 pr-3">Dates / Range</th>
                                    <th className="text-left pb-1 pr-3">Description</th>
                                    <th className="text-left pb-1 pr-3">Original Price</th>
                                    <th className="text-left pb-1">Discounted Price</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {validityPeriods.map((vp, i) => (
                                    <tr key={i} className="border-t border-gray-800">
                                      <td className="py-1.5 pr-3 text-gray-200">
                                        {vp.valid_from || vp.valid_until
                                          ? `${fmt(vp.valid_from)} → ${fmt(vp.valid_until)}`
                                          : Array.isArray(vp.dates)
                                            ? vp.dates.map((dt: string) => fmt(dt)).join(", ")
                                            : "—"}
                                      </td>
                                      <td className="py-1.5 pr-3 text-gray-400">{vp.description ?? "—"}</td>
                                      <td className="py-1.5 pr-3 text-gray-400">
                                        {vp.original_price != null ? `${currency} ${Number(vp.original_price).toLocaleString()}` : "—"}
                                      </td>
                                      <td className="py-1.5 text-gray-200">
                                        {vp.discounted_price != null ? `${currency} ${Number(vp.discounted_price).toLocaleString()}` : "—"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Section>
                        )}

                        {sailings.length > 0 && (
                          <Section title={`Sailings (${sailings.length})`}>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500 uppercase tracking-wide text-[10px]">
                                    <th className="text-left pb-1 pr-3">Departure</th>
                                    <th className="text-left pb-1">Return</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sailings.map((s, i) => (
                                    <tr key={i} className="border-t border-gray-800">
                                      <td className="py-1.5 pr-3 text-gray-200">{fmt(s.departure_date)}</td>
                                      <td className="py-1.5 text-gray-200">{fmt(s.return_date)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Section>
                        )}

                        {portsOfCall.length > 0 && (
                          <Section title={`Ports of Call (${portsOfCall.length})`}>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500 uppercase tracking-wide text-[10px]">
                                    <th className="text-left pb-1 pr-3">Port</th>
                                    <th className="text-left pb-1 pr-3">Arrival</th>
                                    <th className="text-left pb-1">Departure</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {portsOfCall.map((p, i) => (
                                    <tr key={i} className="border-t border-gray-800">
                                      <td className="py-1.5 pr-3 text-gray-200">{p.name ?? p.port ?? "—"}</td>
                                      <td className="py-1.5 pr-3 text-gray-400">{fmt(p.arrival)}</td>
                                      <td className="py-1.5 text-gray-400">{fmt(p.departure)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Section>
                        )}

                        {(startsAt.length > 0 || operatesOn.length > 0) && (
                          <Section title="Experience Schedule">
                            <div className="flex flex-col gap-2">
                              {startsAt.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className="text-[10px] uppercase tracking-wide text-gray-500 w-20 shrink-0">Starts at</span>
                                  <ChipList items={startsAt} className="bg-pink-500/10 text-pink-300" />
                                </div>
                              )}
                              {operatesOn.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className="text-[10px] uppercase tracking-wide text-gray-500 w-20 shrink-0">Operates on</span>
                                  <ChipList items={operatesOn} className="bg-pink-500/5 text-pink-300/80" />
                                </div>
                              )}
                            </div>
                          </Section>
                        )}

                        {blackoutDates.length > 0 && (
                          <Section title={`Blackout Dates (${blackoutDates.length})`}>
                            <div className="flex flex-wrap gap-1.5">
                              {blackoutDates.map((dt, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-300">
                                  {fmt(dt)}
                                </span>
                              ))}
                            </div>
                          </Section>
                        )}

                        {stayPackages.length > 0 && (
                          <Section title={`Hotel Package Validity (${stayPackages.length})`}>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500 uppercase tracking-wide text-[10px]">
                                    <th className="text-left pb-1 pr-3">Package</th>
                                    <th className="text-left pb-1 pr-3">Valid From</th>
                                    <th className="text-left pb-1">Valid Until</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {stayPackages.map((p, i) => (
                                    <tr key={i} className="border-t border-gray-800">
                                      <td className="py-1.5 pr-3 text-gray-200">{p.name ?? p.package_name ?? `Package ${i + 1}`}</td>
                                      <td className="py-1.5 pr-3 text-gray-400">{fmt(p.validity_start)}</td>
                                      <td className="py-1.5 text-gray-400">{fmt(p.validity_end)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Section>
                        )}

                        {mediaScheduledAt && (
                          <Section title="Scheduled At">
                            <p className="text-sm text-gray-200">{fmt(mediaScheduledAt)}</p>
                          </Section>
                        )}

                        {hasServiceDuration && (
                          <Section title="Service Validity / Duration">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                              {visa && (
                                <>
                                  <MetaField label="Visa stay duration" value={visa.stay_duration_days != null ? `${visa.stay_duration_days} days` : undefined} />
                                  <MetaField label="Visa validity" value={visa.validity_days != null ? `${visa.validity_days} days` : undefined} />
                                </>
                              )}
                              {insurance && (
                                <MetaField
                                  label="Insurance max trip duration"
                                  value={insurance.trip_duration_days_max != null ? `${insurance.trip_duration_days_max} days` : undefined}
                                />
                              )}
                              {custom && (
                                <MetaField label="Turnaround" value={custom.turnaround_days != null ? `${custom.turnaround_days} days` : undefined} />
                              )}
                            </div>
                          </Section>
                        )}
                      </div>
                    );
                  })()}

                  {detailTab === "pinecone" && (
                    <PineconeTab
                      loading={pineconeLoading}
                      error={pineconeError}
                      status={pineconeStatus}
                      onReindex={handleReindexPinecone}
                      reindexing={pineconeReindexing}
                      reindexNotice={pineconeReindexNotice}
                    />
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>

    {previewMedia && (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        onClick={() => setPreviewMedia(null)}
      >
        <button
          type="button"
          onClick={() => setPreviewMedia(null)}
          className="absolute top-4 right-4 text-gray-300 hover:text-white transition-colors"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
        <div className="flex flex-col items-center gap-3 max-w-4xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
          {previewMedia.isVideo ? (
            <video src={previewMedia.url} controls autoPlay className="max-w-full max-h-[75vh] rounded-lg" />
          ) : (
            <img src={previewMedia.url} alt={previewMedia.label} className="max-w-full max-h-[75vh] object-contain rounded-lg" />
          )}
          <span className="text-sm text-gray-300">{previewMedia.label}</span>
        </div>
      </div>
    )}

    {rowPublishErrorModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-md shadow-2xl">
          <h2 className="text-lg font-semibold text-white mb-3">
            {rowPublishErrorModal.code === "market_pending_review" ? "Market pending review" : "Can't publish this deal yet"}
          </h2>
          <p className="text-xs text-red-400 whitespace-pre-line">{rowPublishErrorModal.message}</p>
          {(rowPublishErrorModal.pending_countries?.length ?? 0) > 0 && (
            <p className="mt-2 text-xs font-medium text-amber-400">
              Pending: {rowPublishErrorModal.pending_countries!.join(", ")}
            </p>
          )}
          <div className="flex justify-end gap-3 mt-5">
            <button
              onClick={() => setRowPublishErrorModal(null)}
              className="px-4 py-2 bg-gray-800 text-gray-400 rounded-xl hover:bg-gray-700 transition-colors text-sm"
            >
              Close
            </button>
            {rowPublishErrorModal.code === "market_not_covered" && rowPublishErrorModal.dealId && (
              <button
                onClick={handleRowAutoAddMarketAndPublish}
                disabled={rowActionBusy.has(rowPublishErrorModal.dealId)}
                className="px-2.5 py-2 rounded-xl text-sm font-medium bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors disabled:opacity-50"
              >
                {rowActionBusy.has(rowPublishErrorModal.dealId) ? "Adding…" : "Auto-add Market & Publish"}
              </button>
            )}
            {(rowPublishErrorModal.code === "market_not_covered" || rowPublishErrorModal.code === "market_pending_review") && rowPublishErrorModal.dealerId && (
              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  params.set("market_user", rowPublishErrorModal.dealerId!);
                  if (rowPublishErrorModal.dealerName) params.set("market_user_name", rowPublishErrorModal.dealerName);
                  navigate(`/admin/users?${params.toString()}`);
                }}
                className="px-2.5 py-2 rounded-xl text-sm font-medium bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors"
              >
                Add Manually (Market Map)
              </button>
            )}
          </div>
        </div>
      </div>
    )}

    {deleteTarget && (
      <ConfirmModal
        title="Delete Deal"
        description={`"${deleteTarget.title || deleteTarget.id}" will be permanently removed.`}
        items={[
          "Delete deal and all associated media from database",
          "Remove deal from search index (Pinecone)",
        ]}
        confirmLabel="Delete Deal"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    )}
    {alertModal}
    </>
  );
};

export default AdminDealsPage;
